import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  },
});

// Attach the JWT bearer token to every request.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Meeting transcripts sit behind their own passcode (checked by the server),
// entered once per browser session on the Transcripts tab.
export const TRANSCRIPTS_PASSCODE_KEY = 'transcripts_passcode';
api.interceptors.request.use((config) => {
  if ((config.url || '').startsWith('/api/transcripts')) {
    let code: string | null = null;
    try { code = sessionStorage.getItem(TRANSCRIPTS_PASSCODE_KEY); } catch { /* storage blocked */ }
    if (code) {
      config.headers = config.headers || {};
      (config.headers as any)['X-Transcripts-Passcode'] = code;
    }
  }
  return config;
});

// Global fetch patch: many components call raw fetch() instead of this axios
// instance. With auth gating on, those would 401. Inject the bearer token for
// any /api/ request (except login) that doesn't already carry an Authorization
// header. Runs once at module load. SSE/EventSource is unaffected (separate API).
if (typeof window !== 'undefined' && !(window as any).__authFetchPatched) {
  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
      const token = localStorage.getItem('auth_token');
      if (token && url && url.includes('/api/') && !url.includes('/api/auth/login')) {
        const headers = new Headers(
          (init && init.headers) || (input instanceof Request ? input.headers : undefined)
        );
        if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
        init = { ...(init || {}), headers };
      }
    } catch { /* fall through to plain fetch */ }
    return origFetch(input as any, init);
  };
  (window as any).__authFetchPatched = true;
}

// On 401 (expired/invalid token), clear session and bounce to login.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_username');
      // Hard reload to the gated app shell, which will show the landing/login.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export interface PortfolioItem {
  id: number;
  symbol: string;
  name: string;
  shares: number;
  value: number;
  change: number;
  created_at: string;
}

export interface NewsItem {
  id: number;
  title: string;
  summary?: string;
  source: string;
  sentiment: string;
  sentiment_score?: number;
  published_at: string;
  url: string;
  symbol: string;
  related_tickers?: string[];
  category?: string;
  banner_image?: string;
}

// ── Intelligence Hub types ────────────────────────────────────────────────────

export interface NewsArticleDB {
  id: number;
  url: string;
  title: string;
  summary?: string;
  source: string;
  source_url?: string;
  symbol?: string;
  related_tickers: string[];
  category?: string;
  sentiment?: string;
  sentiment_score?: number;
  published_at: string;
  scraped_at: string;
  is_read: boolean;
}

export interface NewsFeedResponse {
  articles: NewsArticleDB[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface TrendingTicker {
  symbol: string;
  count: number;
}

export interface NewsFilters {
  page?: number;
  page_size?: number;
  source?: string;
  category?: string;
  sentiment?: string;
  symbol?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  content_type?: string;
}

export const getNewsFeed = async (filters: NewsFilters = {}): Promise<NewsFeedResponse> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') params.append(key, String(val));
  });
  const response = await api.get(`/api/news/feed?${params.toString()}`);
  return response.data;
};

export const getTrendingTickers = async (hours: number = 24): Promise<TrendingTicker[]> => {
  const response = await api.get(`/api/news/trending-tickers?hours=${hours}`);
  return response.data;
};

export const getNewsSources = async (): Promise<string[]> => {
  const response = await api.get('/api/news/sources');
  return response.data;
};

export const getNewsStats = async (): Promise<{ total: number; unread: number; last_scrape: string | null }> => {
  const response = await api.get('/api/news/stats');
  return response.data;
};

export const markArticleRead = async (articleId: number): Promise<void> => {
  await api.patch(`/api/news/${articleId}/read`);
};

export const triggerNewsScrape = async (): Promise<{ status: string; new_articles: number }> => {
  const response = await api.post('/api/news/scrape');
  return response.data;
};

// ── Twitter Follow Management ──

export interface TwitterFollowItem {
  id: number;
  handle: string;
  display_name?: string;
  added_at?: string;
  is_active: boolean;
}

export const getTwitterFollows = async (): Promise<TwitterFollowItem[]> => {
  const response = await api.get('/api/news/twitter-follows');
  return response.data;
};

export const addTwitterFollow = async (handle: string, display_name?: string): Promise<TwitterFollowItem> => {
  const response = await api.post('/api/news/twitter-follows', { handle, display_name });
  return response.data;
};

export const removeTwitterFollow = async (id: number): Promise<void> => {
  await api.delete(`/api/news/twitter-follows/${id}`);
};

export const toggleTwitterFollow = async (id: number): Promise<{ id: number; is_active: boolean }> => {
  const response = await api.patch(`/api/news/twitter-follows/${id}/toggle`);
  return response.data;
};

export interface AnalyticsItem {
  id: number;
  metric_name: string;
  value: number;
  description: string;
  calculated_at: string;
}

export interface NotificationSettings {
  id: number;
  price_alert_enabled: boolean;
  price_level: number;
  rsi_alert_enabled: boolean;
  rsi_level: number;
}

export interface StockData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  change_percent: number;
  volume: number;
  market_cap: number;
  pe_ratio: number;
  dividend_yield: number;
  timestamp: string;
}

export interface HistoricalData {
  date: string;
  price: number;
  volume: number;
  sma?: number;
  rsi?: number;
}

// Stock Data API
export interface PriceSyncStatus {
  total_stocks: number;
  stocks_with_price: number;
  stocks_with_recent_price: number;
  sync_percentage: number;
  recent_sync_percentage: number;
  latest_price_timestamp: string | null;
  stale_count: number;
  sync_running?: boolean;
}

export const getStockPrice = async (symbol: string): Promise<StockData> => {
  const response = await api.get(`/api/stock/${symbol}`);
  return response.data;
};

export const getPriceSyncStatus = async (): Promise<PriceSyncStatus> => {
  const response = await api.get('/api/prices/status');
  return response.data;
};

export const triggerPriceSync = async (): Promise<{ status: string; message: string }> => {
  const response = await api.post('/api/prices/sync');
  return response.data;
};

export const getStockHistory = async (symbol: string, period: string = "1mo"): Promise<HistoricalData[]> => {
  const response = await api.get(`/api/stock/${symbol}/history?period=${period}`);
  return response.data;
};

export const getPortfolioRealTime = async (symbols: string[]): Promise<StockData[]> => {
  const symbolsParam = symbols.join(',');
  // Add timestamp to prevent caching
  const response = await api.get(`/api/portfolio/real-time?symbols=${symbolsParam}&_t=${Date.now()}`);
  console.log('API Response:', response.data);
  return response.data;
};

export const getRealTimeAnalytics = async (symbols: string[]): Promise<AnalyticsItem[]> => {
  const symbolsParam = symbols.join(',');
  const response = await api.get(`/api/analytics/real-time?symbols=${symbolsParam}`);
  return response.data;
};

export interface BasicRiskMetrics {
  sharpe_ratio: number;
  beta: number;
  alpha: number;
  volatility: number;
  max_drawdown: number;
  sortino_ratio: number;
  annualized_return: number;
  benchmark: string;
  period: string;
}

export const getRiskMetrics = async (
  symbols: string[],
  weights: { [symbol: string]: number },
  benchmark: string = "SPY",
  period: string = "1y"
): Promise<BasicRiskMetrics> => {
  const response = await api.post('/api/analytics/risk-metrics', {
    symbols,
    weights,
    benchmark,
    period
  });
  return response.data;
};

export interface PortfolioValuePoint {
  date: string;
  value: number;
  cost_basis: number;
  pnl: number;
  pnl_percent: number;
}

export interface Order {
  id: string;
  symbol: string;
  shares: number;
  pricePerShare: number;
  date: string;
}

export interface OrdersMap {
  [symbol: string]: Order[];
}

export const getPortfolioValueHistory = async (
  orders: OrdersMap,
  symbols?: string[]
): Promise<PortfolioValuePoint[]> => {
  const response = await api.post('/api/portfolio/value-history', {
    orders,
    symbols
  });
  return response.data;
};

export const getRealTimeNews = async (symbols?: string[]): Promise<NewsItem[]> => {
  const symbolsParam = symbols ? symbols.join(',') : '';
  const response = await api.get(`/api/news/real-time?symbols=${symbolsParam}`);
  return response.data;
};

// Portfolio API
export const getPortfolio = async (): Promise<PortfolioItem[]> => {
  const response = await api.get('/api/portfolio');
  return response.data;
};

export const createPortfolioItem = async (item: Omit<PortfolioItem, 'id' | 'created_at'>): Promise<PortfolioItem> => {
  const response = await api.post('/api/portfolio', item);
  return response.data;
};

// Fear & Greed Index
export interface FearGreedComponent {
  score: number;
  label: string;
  weight: number;
}

export interface FearGreedData {
  score: number;
  label: string;
  components: {
    market_breadth: FearGreedComponent & { detail?: string };
    momentum: FearGreedComponent & { detail?: string };
    volatility: FearGreedComponent & { detail?: string };
    bonds: FearGreedComponent & { detail?: string };
  };
  timestamp: string;
}

export const getFearGreedIndex = async (): Promise<FearGreedData> => {
  const response = await api.get('/api/sentiment/fear-greed');
  return response.data;
};

export const getMarketNews = async (market: string = 'US'): Promise<NewsItem[]> => {
  const response = await api.get(`/api/news/markets?market=${market}`);
  return response.data;
};

export const getCompanyNews = async (symbol: string): Promise<NewsItem[]> => {
  const response = await api.get(`/api/news/company/${symbol}`);
  return response.data;
};

// Earnings Calendar
export interface EarningsItem {
  symbol: string;
  company_name: string;
  earnings_date: string | null;
  eps_estimate: number | null;
  revenue_growth: number | null;
  market_cap: number | null;
  sector: string;
  previous_close: number | null;
  current_price: number | null;
}

export interface EarningsCalendar {
  recent: EarningsItem[];
  this_week: EarningsItem[];
  next_week: EarningsItem[];
  later: EarningsItem[];
  total: number;
  generated_at: string;
}

export const getUpcomingEarnings = async (symbols?: string[]): Promise<EarningsCalendar> => {
  const params = symbols ? `?symbols=${symbols.join(',')}` : '';
  const response = await api.get(`/api/earnings/upcoming${params}`);
  return response.data;
};

// Enhanced Earnings
export interface EarningsBeatRate {
  beats: number;
  total: number;
  rate_str: string;
}

export interface UpcomingEarningsEntry {
  symbol: string;
  company_name: string;
  sector: string;
  country?: string;
  earnings_date: string;
  market_cap: number;
  eps_estimate: number | null;
  revenue_estimate: number | null;
  beat_rate: EarningsBeatRate | null;
}

export interface RecentEarningsEntry {
  symbol: string;
  company_name: string;
  sector: string;
  country?: string;
  earnings_date: string;
  market_cap: number;
  eps_estimate: number | null;
  eps_actual: number | null;
  eps_surprise_pct: number | null;
  revenue_actual: number | null;
  beat_eps: boolean | null;
  post_earnings_move: number | null;
  beat_rate: EarningsBeatRate | null;
  revenue_only?: boolean;
}

export interface EnhancedEarningsData {
  upcoming: UpcomingEarningsEntry[];
  recent: RecentEarningsEntry[];
  generated_at: string;
}

export const getEnhancedEarnings = async (
  daysAhead: number = 14,
  daysBack: number = 7
): Promise<EnhancedEarningsData> => {
  const response = await api.get(
    `/api/market/earnings-enhanced?days_ahead=${daysAhead}&days_back=${daysBack}`
  );
  return response.data;
};

export interface ModelPortfolioEarningsData extends EnhancedEarningsData {
  universe_count: number;
}

export const getModelPortfolioEarnings = async (
  daysAhead: number = 14,
  daysBack: number = 7,
  force: boolean = false
): Promise<ModelPortfolioEarningsData> => {
  const response = await api.get(
    `/api/model-portfolios/earnings?days_ahead=${daysAhead}&days_back=${daysBack}${force ? '&force=true' : ''}`
  );
  return response.data;
};

export const downloadEarningsPdf = async (scope: 'all' | 'portfolio' = 'all', daysAhead = 7, daysBack = 7): Promise<void> => {
  const response = await api.get('/api/earnings/weekly-pdf', {
    params: { scope, days_ahead: daysAhead, days_back: daysBack },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `earnings_calendar.pdf`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// News API
export const getNews = async (): Promise<NewsItem[]> => {
  const response = await api.get('/api/news');
  return response.data;
};

export const createNewsItem = async (item: Omit<NewsItem, 'id'>): Promise<NewsItem> => {
  const response = await api.post('/api/news', item);
  return response.data;
};

// Analytics API
export const getAnalytics = async (): Promise<AnalyticsItem[]> => {
  const response = await api.get('/api/analytics');
  return response.data;
};

export const createAnalyticsItem = async (item: Omit<AnalyticsItem, 'id' | 'calculated_at'>): Promise<AnalyticsItem> => {
  const response = await api.post('/api/analytics', item);
  return response.data;
};

// Notification Settings API
export const getNotificationSettings = async (): Promise<NotificationSettings> => {
  const response = await api.get('/api/notifications');
  return response.data;
};

export const updateNotificationSettings = async (settings: Omit<NotificationSettings, 'id'>): Promise<NotificationSettings> => {
  const response = await api.put('/api/notifications', settings);
  return response.data;
};

// Technical Analysis Data
export interface TechnicalData {
  symbol: string;
  name?: string;
  current_price: number;
  ma_20: number | null;
  ma_50: number | null;
  ma_200: number | null;
  pct_from_ma_20: number | null;
  pct_from_ma_50: number | null;
  pct_from_ma_200: number | null;
  high_52w: number | null;
  low_52w: number | null;
  pct_from_52w_high: number | null;
  pct_from_52w_low: number | null;
  historical_volatility: number | null;
  rsi: number | null;
  momentum: number | null;
}

export const getTechnicalIndicators = async (symbols: string[]): Promise<TechnicalData[]> => {
  const symbolsParam = symbols.join(',');
  const response = await api.get(`/api/analytics/technical?symbols=${symbolsParam}&_t=${Date.now()}`);
  return response.data;
};

// Technical Chart Data (with MAs and Bollinger Bands)
export interface TechnicalChartPoint {
  date: string;
  price: number;
  open: number;
  high: number;
  low: number;
  volume: number;
  ma_20: number | null;
  ma_50: number | null;
  ma_200: number | null;
  bb_upper: number | null;
  bb_middle: number | null;
  bb_lower: number | null;
  rsi: number | null;
  macd: number | null;
  macd_signal: number | null;
  macd_histogram: number | null;
  stoch_k: number | null;
  stoch_d: number | null;
  williams_r: number | null;
  cci: number | null;
  atr: number | null;
  obv: number | null;
  vwap: number | null;
  mfi: number | null;
  adx: number | null;
  plus_di: number | null;
  minus_di: number | null;
  pivot: number | null;
  resistance_1: number | null;
  support_1: number | null;
  resistance_2: number | null;
  support_2: number | null;
}

export interface FibonacciLevels {
  fib_0: number;    // 0% (High)
  fib_236: number;  // 23.6%
  fib_382: number;  // 38.2%
  fib_500: number;  // 50%
  fib_618: number;  // 61.8%
  fib_786: number;  // 78.6%
  fib_100: number;  // 100% (Low)
}

export interface TechnicalEvent {
  date: string;
  type: string;
  title: string;
  description: string;
  sentiment: 'bullish' | 'bearish';
}

export interface ElliottWave {
  date: string;
  price: number;
  label: string;  // '1', '2', '3', '4', '5', 'A', 'B', 'C'
  type: 'impulse' | 'corrective';
  swing_type: 'high' | 'low';
  index: number;
}

export interface TechnicalChartResponse {
  data: TechnicalChartPoint[];
  fibonacci: FibonacciLevels;
  period_high: number;
  period_low: number;
  interval: string;  // "1h" or "1d"
  events: TechnicalEvent[];
  elliott_waves: ElliottWave[];
  company_name?: string;
}

export const getTechnicalChartData = async (symbol: string, period: string = "1y"): Promise<TechnicalChartResponse> => {
  const response = await api.get(`/api/analytics/technical-chart/${symbol}?period=${period}&_t=${Date.now()}`);
  return response.data;
};

// ── Signals digest (dashboard) ──────────────────────────────────────────────

export interface DigestSignal {
  symbol: string;
  name: string | null;
  country: string;
  signal_type: string;
  direction: string;
  detail: string | null;
  change_pct: number | null;
  signal_date: string;
  market_cap_usd: number | null;
  watched?: boolean;
}

export const getSignalsDigest = async (
  params: { country?: string; direction?: string; min_cap_usd?: number; limit?: number } = {}
): Promise<{ signals: DigestSignal[]; total_matched: number; last_updated: string | null }> => {
  const q = new URLSearchParams();
  if (params.country) q.set('country', params.country);
  if (params.direction) q.set('direction', params.direction);
  if (params.min_cap_usd != null) q.set('min_cap_usd', String(params.min_cap_usd));
  if (params.limit != null) q.set('limit', String(params.limit));
  const r = await api.get(`/api/signals/digest?${q.toString()}`);
  return r.data;
};

// ── Chart Patterns API ──────────────────────────────────────────────────────

export interface PatternGeometry {
  pivots: { date: string; price: number }[];
  lines: { name: string; points: { date: string; price: number }[] }[];
  /** downsampled context closes stored at scan time — renders charts with zero fetches */
  series?: { d: string; p: number }[];
}

export interface ChartPatternRow {
  id: number;
  symbol: string;
  name: string | null;
  country: string;
  sector: string | null;
  market_cap_usd: number | null;
  pattern_type: string;
  timeframe: string;
  confluence: boolean;
  /** 0=breaking now, 1=at trigger, 2=confirmed, 3=forming */
  tier: number;
  volume_confirmed: boolean;
  breakout_volume_ratio: number | null;
  /** expected value in risk units (R), from backtested probs x this row's R:R; null = no reliable stats */
  expected_r: number | null;
  direction: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  confirmed_date: string | null;
  last_price: number;
  neckline_price: number;
  target_price: number;
  magnitude_pct: number;
  geometry: PatternGeometry | null;
}

export const getPatterns = async (params: {
  pattern?: string; status?: string; direction?: string; country?: string;
  timeframe?: string; confluence?: boolean; tier?: number; max_tier?: number;
  min_cap_usd?: number; limit?: number; symbol?: string;
} = {}): Promise<{ patterns: ChartPatternRow[]; total_matched: number; last_scanned: string | null }> => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') q.set(k, String(v)); });
  const r = await api.get(`/api/patterns?${q.toString()}`);
  return r.data;
};

export const getPatternCounts = async (country: string = 'all', min_cap_usd: number = 5e8, timeframe: string = ''):
  Promise<{ patterns: Record<string, { forming: number; confirmed: number }>; tiers: Record<number, number> }> => {
  const r = await api.get(`/api/patterns/counts?country=${country}&min_cap_usd=${min_cap_usd}${timeframe ? `&timeframe=${timeframe}` : ''}`);
  return r.data;
};

// ── Watchlist (the spine's core primitive) ──────────────────────────────────

export interface WatchlistItemView {
  symbol: string; name: string | null; sources: string[]; muted: boolean;
  price: number | null; chg_1d: number | null;
  added_price: number | null; since_added_pct: number | null;
  added_at: string | null; note: string | null;
}

export const getWatchlistItems = async (): Promise<WatchlistItemView[]> => {
  const r = await api.get('/api/watchlist');
  return r.data.items || [];
};
export const starSymbol = async (symbol: string) => {
  const r = await api.post('/api/watchlist', { symbol });
  updateWatchedCache(symbol, true);
  return r.data;
};
export const unstarSymbol = async (symbol: string) => {
  const r = await api.delete(`/api/watchlist/${encodeURIComponent(symbol)}`);
  if (r.data?.status === 'unwatched') updateWatchedCache(symbol, false);
  return r.data;
};
export const syncWatchlistUp = async (portfolio: string[], watchlist: string[]) => {
  const r = await api.post('/api/watchlist/sync', { portfolio, watchlist });
  return r.data;
};

// Module-level watched-set cache so star buttons render instantly everywhere.
let _watchedSet: Set<string> | null = null;
export const getWatchedSet = async (force = false): Promise<Set<string>> => {
  if (_watchedSet && !force) return _watchedSet;
  try {
    const items = await getWatchlistItems();
    _watchedSet = new Set(items.filter(i => !i.muted).map(i => i.symbol));
  } catch { _watchedSet = _watchedSet || new Set(); }
  return _watchedSet;
};
export const updateWatchedCache = (symbol: string, watched: boolean) => {
  if (_watchedSet) { watched ? _watchedSet.add(symbol.toUpperCase()) : _watchedSet.delete(symbol.toUpperCase()); }
};

// ── Sector performance ticker (dashboard rolling banner) ────────────────────

export interface SectorPerf {
  country: string; country_label: string; sector: string;
  chg_pct: number; n: number; total_cap_usd: number;
}
export const getSectorPerformance = async (): Promise<{ sectors: SectorPerf[]; last_updated: string | null }> => {
  const r = await api.get('/api/market/sector-performance');
  return r.data;
};

// ── Telegram push (Phase 2 of the spine) ────────────────────────────────────

export interface TelegramStatus {
  configured: boolean; linked: boolean; linked_at: string | null; bot_username: string | null;
}
export const getTelegramStatus = async (): Promise<TelegramStatus> => {
  const r = await api.get('/api/telegram/status'); return r.data;
};
export const getTelegramLinkCode = async (): Promise<{ code: string; bot_username: string | null; expires_in: number }> => {
  const r = await api.post('/api/telegram/link-code'); return r.data;
};
export const sendTelegramTest = async (): Promise<{ sent: boolean }> => {
  const r = await api.post('/api/telegram/test'); return r.data;
};
export const unlinkTelegram = async () => {
  const r = await api.delete('/api/telegram/link'); return r.data;
};

// ── Research Agent (chat-based research: company + macro + follow-ups) ─────

export interface ResearchThreadSummary {
  id: number; title: string; mode: string; symbol: string | null;
  created_at: string | null; updated_at: string | null;
}
export interface ResearchMessageView {
  id: number; role: 'user' | 'assistant'; content: string;
  tools_used: { name: string; args: any }[]; created_at: string | null;
}

export const listResearchThreads = async (): Promise<ResearchThreadSummary[]> => {
  const r = await api.get('/api/research-agent/threads');
  return r.data.threads || [];
};
export const getResearchThread = async (id: number): Promise<{ thread: ResearchThreadSummary; messages: ResearchMessageView[] }> => {
  const r = await api.get(`/api/research-agent/threads/${id}`);
  return r.data;
};
export const createResearchThread = async (query: string, mode: 'company' | 'macro', symbol?: string):
  Promise<{ thread: ResearchThreadSummary; messages: ResearchMessageView[] }> => {
  const r = await api.post('/api/research-agent/threads', { query, mode, symbol });
  return r.data;
};
export const sendResearchMessage = async (threadId: number, content: string): Promise<{ messages: ResearchMessageView[] }> => {
  const r = await api.post(`/api/research-agent/threads/${threadId}/messages`, { content });
  return r.data;
};
export const deleteResearchThread = async (threadId: number) => {
  const r = await api.delete(`/api/research-agent/threads/${threadId}`);
  return r.data;
};

// ── Chart auto-annotations ──────────────────────────────────────────────────

export interface AnnotationPoint { date: string; price: number }
export interface ChartAnnotations {
  levels: { price: number; touches: number; last_touch: string; kind: string; major?: boolean }[];
  trendlines: { kind: string; touches: number; last_touch?: string; points: AnnotationPoint[] }[];
  channels: { direction: string; touches: number; upper: AnnotationPoint[]; lower: AnnotationPoint[] }[];
  patterns?: { pattern_type: string; direction: string; status: string; timeframe: string; target_price: number; geometry: PatternGeometry | null }[];
  detail?: string;
}

export const getChartAnnotations = async (symbol: string, lookbackDays?: number): Promise<ChartAnnotations> => {
  const q = lookbackDays ? `?lookback_days=${lookbackDays}` : '';
  const r = await api.get(`/api/chart/${encodeURIComponent(symbol)}/annotations${q}`);
  return r.data;
};

// ── Admin / Users API ───────────────────────────────────────────────────────

export interface AdminUser {
  id: number;
  username: string;
  email: string | null;
  role: string;
  is_active: boolean;
  created_at: string | null;
  last_login: string | null;
}

export const adminListUsers = async (): Promise<AdminUser[]> => {
  const r = await api.get('/api/admin/users');
  return r.data;
};

export const adminCreateUser = async (u: { username: string; password: string; email?: string; role?: string }) => {
  const r = await api.post('/api/admin/users', u);
  return r.data;
};

export const adminDeleteUser = async (id: number) => {
  const r = await api.delete(`/api/admin/users/${id}`);
  return r.data;
};

export const adminUpdateUser = async (id: number, patch: { is_active?: boolean; role?: string; password?: string }) => {
  const r = await api.patch(`/api/admin/users/${id}`, patch);
  return r.data;
};

// ── Interactive Valuation (DCF) API ─────────────────────────────────────────

export interface DcfAssumptions {
  base_fcf: number;
  base_fcf_raw: number;
  use_normalized: boolean;
  stage1_growth: number;       // raw decimals (0.085 = 8.5%)
  growth_source: string;
  terminal_growth: number;
  wacc: number;
  risk_free_rate: number;
  beta: number;
  erp: number;
  hist_fcf_cagr: number | null;
  shares_outstanding: number;
  net_debt_raw: number;
  price_to_financial: number;
  margin_glide_active: boolean;
  current_fcf_margin: number | null;
  hist_avg_fcf_margin: number | null;
  target_fcf_margin: number | null;
  margin_glide_years: number | null;
}

// Full transparency on how much of the fair value rests on the terminal
// assumption vs. the explicit forecast period — shared shape across both
// the standard DCF and the Growth DCF (each has its own terminal-year cash
// flow definition, but the breakdown fields mean the same thing in both).
export interface TerminalValueBreakdown {
  terminal_year_fcf: string;          // formatted, e.g. "$277.5B"
  terminal_year_fcf_raw: number;
  terminal_value_raw: string;         // undiscounted, Year-N dollars
  pv_terminal_value: string;          // discounted back to today
  pv_explicit_fcf: string;            // PV of the explicit forecast years
  terminal_value_pct_of_ev: number | null;   // % of EV from the terminal assumption
  explicit_fcf_pct_of_ev: number | null;     // % of EV from the explicit forecast (complement)
  implied_terminal_multiple: number | null;  // TV ÷ terminal-year FCF — null if terminal FCF <= 0
  wacc_terminal_spread: number;       // WACC − terminal growth, percentage points (small = fragile)
}

export interface DcfResult {
  // Present instead of every other field when FCF is currently negative and
  // there's neither enough profitable history (>=3yr) nor a target_fcf_margin
  // override to glide toward — prompt the user for one rather than showing
  // nothing (see ValuationTab's needs_target_margin handling).
  needs_target_margin?: boolean;
  symbol?: string;
  methodology: string;
  currency: string;
  financial_currency: string;
  wacc: number;                 // percent (e.g. 10.39)
  wacc_components: {
    risk_free_rate: number; equity_risk_premium: number; beta: number;
    cost_of_equity: number; cost_of_debt_pretax: number; cost_of_debt_aftertax: number;
    tax_rate: number; debt_weight: number; equity_weight: number;
  };
  projected_fcf: { year: number; fcf: string; growth_rate: string }[];
  terminal_growth_rate: number;
  terminal_value: string;
  enterprise_value: string;
  net_debt: string;
  equity_value: string;
  shares_outstanding: string;
  fair_value_per_share: number;
  current_price: number;
  upside_downside: string;
  terminal_value_breakdown: TerminalValueBreakdown;
  sensitivity_table: { wacc: number; terminal_growth: number; fair_value: number }[];
  assumptions: DcfAssumptions;
  // Also present on the needs_target_margin partial response:
  current_fcf_margin?: number | null;
  hist_avg_fcf_margin?: number | null;
}

export interface DcfOverrides {
  stage1_growth?: number;
  terminal_growth?: number;
  wacc?: number;
  risk_free_rate?: number;
  beta?: number;
  erp?: number;
  base_fcf?: number;
  target_fcf_margin?: number;
  margin_glide_years?: number;
}

export interface ReverseDcfResult {
  symbol: string;
  target_price: number;
  implied_stage1_growth: number | null;
  solvable: boolean;
  base_stage1_growth: number;
  growth_source: string;
  hist_fcf_cagr: number | null;
  wacc?: number;
  terminal_growth?: number;
  currency?: string;
}

export const getDcf = async (symbol: string): Promise<DcfResult> => {
  const response = await api.get(`/api/valuation/${symbol}/dcf`);
  return response.data;
};

export const recomputeDcf = async (symbol: string, overrides: DcfOverrides): Promise<DcfResult> => {
  const response = await api.post(`/api/valuation/${symbol}/dcf`, { overrides });
  return response.data;
};

export const getReverseDcf = async (symbol: string, targetPrice?: number, overrides?: DcfOverrides): Promise<ReverseDcfResult> => {
  const response = await api.post(`/api/valuation/${symbol}/reverse-dcf`, { target_price: targetPrice, overrides });
  return response.data;
};

// Multiples-implied price target — mean reversion to this stock's own
// historical average P/E / EV-EBITDA, applied to forward estimates. A
// market-anchored cross-check that's meant to sit alongside (and often
// disagree with) the pure cash-flow DCF fair value.
export interface MultiplesTargetResult {
  symbol: string;
  currency: string;
  current_price: number;
  data_source: 'edgar' | 'yfinance';
  pe_implied_price: number | null;
  pe_upside: number | null;      // percent
  hist_avg_pe: number | null;
  current_pe: number | null;
  forward_eps: number | null;         // 1yr-fwd EPS actually used (trailing × applied growth)
  analyst_forward_eps: number | null; // yfinance's own forward EPS estimate, for reference
  pe_years_used: number;
  ev_ebitda_implied_price: number | null;
  ev_ebitda_upside: number | null;  // percent
  hist_avg_ev_ebitda: number | null;
  current_ev_ebitda: number | null;
  implied_ebitda_growth: number | null;  // = applied growth (shared across all legs)
  ev_ebitda_years_used: number;
  // EV/Revenue: requires neither positive earnings nor positive EBITDA, so
  // this is often the ONLY populated leg for pre-profit growth names.
  ev_revenue_implied_price: number | null;
  ev_revenue_upside: number | null;  // percent
  hist_avg_ev_revenue: number | null;
  current_ev_revenue: number | null;
  ev_revenue_years_used: number;
  growth_source: string;
  blended_target: number | null;
  blended_upside: number | null;  // percent
  methodology: string;
}

// growthOverride: pass the DCF panel's current Stage-1 growth (raw decimal)
// so this target moves with the same slider/scenario as the DCF fair value
// sitting next to it, instead of using a fixed, disconnected growth figure.
export const getMultiplesTarget = async (symbol: string, growthOverride?: number): Promise<MultiplesTargetResult> => {
  const params = growthOverride != null ? `?growth=${growthOverride}` : '';
  const response = await api.get(`/api/valuation/${symbol}/multiples-target${params}`);
  return response.data;
};

// ── Peer-Multiple Regression ── EV/Revenue regressed against forward growth
// across a peer group, instead of reverting the stock to its OWN historical
// multiple (which MultiplesTargetResult does). New, independent method —
// does not replace or share state with getMultiplesTarget.
export interface PeerRegressionPoint {
  symbol: string;
  growth: number;       // raw decimal
  ev_revenue: number;
}
export interface PeerRegressionResult {
  insufficient_peer_data?: boolean;
  n_peers_found?: number;
  symbol: string;
  sector: string | null;
  industry: string | null;
  peer_source: 'ai_peers' | 'db_peers' | null;
  n_peers: number;
  r_squared: number;
  slope: number;
  intercept: number;
  target_growth: number;          // raw decimal
  implied_ev_revenue_multiple: number;
  multiple_meaningful: boolean;
  is_extrapolated: boolean;
  peer_growth_range: [number, number];  // raw decimals
  current_price: number | null;
  implied_price: number | null;
  upside_downside: string | null;
  peer_points: PeerRegressionPoint[];
  methodology: string;
}
export const getPeerRegression = async (symbol: string, growthOverride?: number): Promise<PeerRegressionResult> => {
  const params = growthOverride != null ? `?growth=${growthOverride}` : '';
  const response = await api.get(`/api/valuation/${symbol}/peer-regression${params}`);
  return response.data;
};
export const recomputePeerRegression = async (symbol: string, targetGrowthOverride?: number): Promise<PeerRegressionResult> => {
  const response = await api.post(`/api/valuation/${symbol}/peer-regression`, { target_growth_override: targetGrowthOverride });
  return response.data;
};

// ── Growth-Company DCF (Damodaran-style) ── revenue-driven, operating margin
// converges to a PEER-derived target (not the stock's own history), and
// reinvestment is derived from a sales-to-capital ratio rather than backed
// into via FCF margin. Independent engine from DcfResult — does not touch it.
export interface GrowthDcfOverrides {
  stage1_growth?: number;
  terminal_growth?: number;
  wacc?: number;
  risk_free_rate?: number;
  beta?: number;
  erp?: number;
  target_operating_margin?: number;
  sales_to_capital?: number;
  margin_convergence_years?: number;
  tax_rate?: number;
}
export interface GrowthDcfResult {
  symbol: string;
  currency: string;
  financial_currency: string;
  wacc: number;                    // percent
  current_operating_margin: number;  // percent
  target_operating_margin: number;   // percent
  margin_source: string;
  margin_convergence_years: number;
  sales_to_capital: number;
  sales_to_capital_source: string;
  stage1_growth: number;           // percent
  growth_source: string;
  terminal_growth_rate: number;    // percent
  tax_rate: number;                // percent
  projected_fcf: { year: number; fcf: string; growth_rate: string; margin: string }[];
  terminal_value: string;
  enterprise_value: string;
  net_debt: string;
  equity_value: string;
  shares_outstanding: string;
  fair_value_per_share: number;
  current_price: number;
  upside_downside: string;
  terminal_value_breakdown: TerminalValueBreakdown;
  peer_source: 'ai_peers' | 'db_peers' | null;
  peer_industry: string | null;
  n_peers_for_margin: number;
  methodology: string;
  assumptions: {
    stage1_growth: number; terminal_growth: number; wacc: number;
    risk_free_rate: number; beta: number; erp: number;
    target_operating_margin: number; sales_to_capital: number;
    margin_convergence_years: number; tax_rate: number;
    current_operating_margin: number; shares_outstanding: number;
    net_debt_raw: number; price_to_financial: number;
  };
}
export const getGrowthDcf = async (symbol: string): Promise<GrowthDcfResult> => {
  const response = await api.get(`/api/valuation/${symbol}/growth-dcf`);
  return response.data;
};
export const recomputeGrowthDcf = async (symbol: string, overrides: GrowthDcfOverrides): Promise<GrowthDcfResult> => {
  const response = await api.post(`/api/valuation/${symbol}/growth-dcf`, { overrides });
  return response.data;
};

// Efficient Frontier API
export interface EfficientFrontierPoint {
  risk: number;  // Volatility as percentage
  return: number;  // Expected return as percentage
  sharpe: number;
  weights: { [symbol: string]: number };
  strategy?: string;
  /** True when the optimizer failed to converge and this point is an
   * equal-weight fallback rather than a genuine solve for its strategy. */
  used_fallback?: boolean;
}

export interface IndividualAsset {
  symbol: string;
  name?: string;
  risk: number;
  /** Mean daily return x 252 — the optimiser's input, and what the chart plots. */
  return: number;
  /** What the stock actually compounded to over the window, annualised. Differs
   *  from `return` by roughly variance/2, in either direction. */
  compound_return?: number | null;
  weight: number;
}

export interface BenchmarkAsset {
  symbol: string;
  name: string;
  risk: number;
  return: number;
  sharpe: number;
  /** Currency the benchmark ETF is priced in. */
  currency?: string;
  /** primary = the basket's own market; secondary = large global ETFs, often
   *  in a different currency; portfolio = the user's current holdings. */
  tier?: 'primary' | 'secondary' | 'portfolio';
  /** Set when its prices were converted into the basket's currency. */
  converted_from?: string | null;
  /** Your portfolio only: 'value' = shares x latest price; 'given' = weights supplied. */
  weight_basis?: 'value' | 'given';
  weights?: Record<string, number>;
}

export interface EfficientFrontierEstimation {
  /** Ledoit-Wolf intensity in [0,1]: 0 = sample covariance kept, 1 = fully shrunk. */
  shrinkage_intensity: number;
  /** Per-asset ceiling actually applied. */
  max_weight: number;
  /** True when the requested cap was infeasible and had to be raised to 1/n. */
  max_weight_raised: boolean;
  requested_max_weight: number;
}

export interface EfficientFrontierResponse {
  estimation?: EfficientFrontierEstimation;
  frontier_points: EfficientFrontierPoint[];
  min_variance_portfolio: EfficientFrontierPoint | null;
  max_sharpe_portfolio: EfficientFrontierPoint | null;
  risk_parity_portfolio?: EfficientFrontierPoint | null;
  min_cvar_portfolio?: EfficientFrontierPoint | null;
  max_diversification_portfolio?: EfficientFrontierPoint | null;
  inverse_volatility_portfolio?: EfficientFrontierPoint | null;
  individual_assets: IndividualAsset[];
  benchmarks?: BenchmarkAsset[];
  /** Currency of the basket's dominant market; null for a global basket. */
  base_currency?: string | null;
  /** Short-term risk-free rate used for every Sharpe ratio and the CML, with
   *  provenance. `rate` is in percent. */
  /** The window and frequency every estimate rests on. */
  sample?: {
    start: string;
    end: string;
    observations: number;
    frequency: 'daily' | 'weekly';
    frequency_reason: string;
    /** A holding whose short history set the start of the window, if any. */
    limited_by: string | null;
  };
  /** Every figure in the response is in this currency. */
  currency?: string;
  /** Holdings converted into `currency`, mapped to the currency they trade in. */
  fx_converted?: Record<string, string>;
  /** Holdings with no available exchange rate, left in their own currency. */
  fx_failed?: string[];
  risk_free?: {
    rate: number;
    country: string;
    tenor: string;
    source: string;
    as_of: string | null;
    live: boolean;
  };
  risk_free_rate: number;
  period: string;
  error?: string;
}

// Monte Carlo types
export interface MonteCarloSummary {
  expected_terminal_value: number;
  median_terminal_value: number;
  ci_90_lower: number;
  ci_90_upper: number;
  prob_loss: number;
  prob_doubling: number;
  best_case_5pct: number;
  worst_case_5pct: number;
}

export interface MonteCarloResponse {
  summary: MonteCarloSummary;
  /** Portfolio value percentiles at each month: p5, p25, p50, p75, p95. */
  percentile_bands?: Record<'p5' | 'p25' | 'p50' | 'p75' | 'p95', number[]>;
  time_labels?: string[];
  initial_value: number;
  horizon_years: number;
  num_simulations: number;
  input_params: {
    annualized_return: number;
    annualized_volatility: number;
    symbols: string[];
    weights: { [symbol: string]: number };
  };
  error?: string;
}

export const getMonteCarloSimulation = async (
  symbols: string[],
  weights: { [symbol: string]: number },
  horizon_years: number = 5,
  initial_value: number = 100000,
  period: string = '3y'
): Promise<MonteCarloResponse> => {
  const response = await api.post('/api/analytics/monte-carlo', {
    symbols,
    weights,
    horizon_years,
    initial_value,
    period,
  });
  return response.data;
};

export const getEfficientFrontier = async (
  symbols: string[],
  period: string = "1y",
  num_portfolios: number = 100,
  userPortfolioSymbols?: string[],
  userPortfolioWeights?: { [symbol: string]: number },
  rfCountry?: string,
  /** Per-asset ceiling as a fraction. Raised server-side if infeasible for the
   *  number of assets; check `estimation.max_weight_raised` on the response. */
  maxWeight?: number,
  /** Share counts per symbol: the server weights them by current value. */
  userPortfolioShares?: { [symbol: string]: number }
): Promise<EfficientFrontierResponse> => {
  const response = await api.post('/api/analytics/efficient-frontier', {
    symbols,
    period,
    num_portfolios,
    user_portfolio_symbols: userPortfolioSymbols,
    user_portfolio_weights: userPortfolioWeights,
    risk_free_country: rfCountry && rfCountry !== 'AUTO' ? rfCountry : undefined,
    max_weight: maxWeight,
    user_portfolio_shares: userPortfolioShares,
  });
  return response.data;
};

// Trade Recommendations API
export interface TradeRecommendationFactor {
  score: number;
  detail: string;
}

export interface TradeRecommendation {
  symbol: string;
  name: string;
  current_weight: number;
  target_weight: number;
  suggested_weight: number;
  weight_change: number;
  recommendation: 'Strong Buy' | 'Buy' | 'Hold' | 'Sell' | 'Strong Sell';
  score: number;
  factors: Record<string, TradeRecommendationFactor>;
  key_factors: string[];
}

export interface TradeRecommendationsResponse {
  holdings: TradeRecommendation[];
  portfolio_insights: string[];
}

export const getTradeRecommendations = async (
  portfolioId: number,
  timeframe: string = '1Y',
  benchmarks?: string[]
): Promise<TradeRecommendationsResponse> => {
  const body: Record<string, unknown> = { timeframe };
  if (benchmarks?.length) body.benchmarks = benchmarks;
  const response = await api.post(`/api/model-portfolios/${portfolioId}/recommendations`, body);
  return response.data;
};

// Research API
export interface NewsItemShort {
  title: string;
  url: string;
  source: string;
  published_at: string;
}

export interface ChartDataPoint {
  date: string;
  price: number;
  ma_20?: number;
  ma_50?: number;
  ma_200?: number;
}

export interface PerformanceMetrics {
  week_1?: number;
  month_1?: number;
  month_3?: number;
  ytd?: number;
  year_1?: number;
}

export interface TechnicalSummary {
  rsi?: number | null;
  rsi_label?: string;
  momentum?: number | null;
  volatility?: number | null;
  volatility_label?: string;
  implied_volatility?: number | null;
  iv_label?: string;
  ma_20?: number | null;
  ma_50?: number | null;
  ma_200?: number | null;
  price_vs_ma20?: number | null;
  price_vs_ma50?: number | null;
  price_vs_ma200?: number | null;
}

export interface TechnicalEvent {
  date: string;
  type: string;
  title: string;
  description: string;
  sentiment: 'bullish' | 'bearish';
}

export interface FinancialTableItem {
  metric: string;
  value: string;
}

export interface DCFAnalysis {
  methodology?: string;
  wacc?: number;
  wacc_components?: {
    risk_free_rate?: number;
    equity_risk_premium?: number;
    beta?: number;
    cost_of_equity?: number;
    cost_of_debt?: number;
    tax_rate?: number;
    debt_weight?: number;
    equity_weight?: number;
  };
  projected_fcf?: Array<{
    year: number;
    fcf: string;
    growth_rate: string;
  }>;
  terminal_growth_rate?: number;
  terminal_value?: string;
  enterprise_value?: string;
  net_debt?: string;
  equity_value?: string;
  shares_outstanding?: string;
  fair_value_per_share?: number;
  current_price?: number;
  upside_downside?: string;
  sensitivity_table?: Array<{
    wacc: number;
    terminal_growth: number;
    fair_value: number;
  }>;
  narrative?: string;
}

export interface CFAAnalysis {
  balance_sheet: string;
  balance_sheet_table?: FinancialTableItem[];
  profit_loss: string;
  income_statement_table?: FinancialTableItem[];
  cash_flow: string;
  cash_flow_table?: FinancialTableItem[];
  analyst_consensus: string;
  swot: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  peer_comparison: {
    peers: Array<{
      name: string;
      pe_ratio: number;
      ev_ebitda: number;
      price_book: number;
      sales_book: number;
    }>;
    analysis: string;
  };
}

export interface FinancialStatementTable {
  periods: string[];
  rows: Array<{
    metric: string;
    values: (string | null)[];
  }>;
}

export interface StockResearchResponse {
  symbol: string;
  company_name: string;
  sector?: string;
  current_price?: number;
  price_change?: number;
  ai_summary: string;
  key_insights?: string[];
  analysis?: {
    fundamentals?: string;
    technicals?: string;
    risks?: string;
  };
  key_stats?: {
    market_cap?: string;
    pe_ratio?: string;
    dividend_yield?: string;
    week_52_high?: number;
    week_52_low?: number;
    volume?: string;
  };
  chart_data?: ChartDataPoint[];
  recent_news?: NewsItemShort[];
  upcoming_events?: {
    date: string;
    event: string;
  }[];
  recommendation?: {
    rating: 'BUY' | 'HOLD' | 'SELL';
    summary: string;
  };
  analyst_recommendations?: {
    has_data: boolean;
    consensus?: string;
    num_analysts?: number;
    recommendation_trend?: 'improving' | 'declining' | 'stable';
    breakdown: {
      strong_buy: number;
      buy: number;
      hold: number;
      sell: number;
      strong_sell: number;
    };
  };
  price_targets?: {
    mean: number;
    high?: number;
    low?: number;
    num_analysts?: number;
    upside?: number;
  };
  fundamentals?: {
    has_data: boolean;
    valuation: {
      market_cap?: number;
      pe_ratio?: number;
      forward_pe?: number;
      peg_ratio?: number;
      price_to_book?: number;
      price_to_sales?: number;
      ev_to_revenue?: number;
      ev_to_ebitda?: number;
    };
    profitability: {
      profit_margin?: number;
      operating_margin?: number;
      gross_margin?: number;
      roe?: number;
      roa?: number;
      revenue_ttm?: number;
      net_income_ttm?: number;
      ebitda?: number;
    };
    financial_health: {
      total_cash?: number;
      total_debt?: number;
      current_ratio?: number;
      debt_to_equity?: number;
      free_cash_flow?: number;
    };
    growth: {
      revenue_growth?: number;
      earnings_growth?: number;
      eps_ttm?: number;
      eps_forward?: number;
    };
  };
  cfa_analysis?: CFAAnalysis;
  dcf_analysis?: DCFAnalysis;
  financial_statements?: {
    income_statement: { annual: FinancialStatementTable; quarterly: FinancialStatementTable };
    balance_sheet:    { annual: FinancialStatementTable; quarterly: FinancialStatementTable };
    cash_flow:        { annual: FinancialStatementTable; quarterly: FinancialStatementTable };
  };
  performance?: PerformanceMetrics;
  technical_summary?: TechnicalSummary;
  technical_events?: TechnicalEvent[];
}

export const getStockResearch = async (symbol: string, refresh = false, cacheOnly = false): Promise<StockResearchResponse | null> => {
  const params: Record<string, any> = {};
  if (refresh) params.refresh = true;
  if (cacheOnly) params.cache_only = true;
  const response = await api.get(`/api/research/${symbol}`, {
    params: Object.keys(params).length ? params : undefined,
    timeout: 300000 // 5 minutes
  });
  return response.data;
};

// Saved research reports
export interface SavedReportSummary {
  id: number;
  symbol: string;
  company_name: string;
  current_price: number | null;
  recommendation: string | null;
  trading_symbol: string;
  sector: string | null;
  industry: string | null;
  created_at: string;
}

export const getSavedReports = async (): Promise<SavedReportSummary[]> => {
  const response = await api.get('/api/research/reports');
  return response.data;
};

export const getSavedReport = async (id: number): Promise<StockResearchResponse> => {
  const response = await api.get(`/api/research/reports/${id}`);
  return response.data;
};

export const deleteSavedReport = async (id: number): Promise<void> => {
  await api.delete(`/api/research/reports/${id}`);
};


// ============================================================================
// TREEMAP TYPES AND API
// ============================================================================

export interface TreemapNode {
  name: string;
  full_name?: string;
  value: number;
  change: number;
  children?: TreemapNode[];
  sector?: string;
  industry?: string;
  price?: number;
  stock_count?: number;
}

export interface TreemapMetadata {
  timeframe: string;
  level: string;
  generated_at: string;
  total_stocks?: number;
  market_name?: string;
  change_source?: 'index' | 'constituents';   // what `change` on the whole map is
}

export interface TreemapData {
  name: string;
  children: TreemapNode[];
  value?: number;
  change?: number;
  constituent_change?: number;   // the tiles summed, when `change` is the index's own move
  metadata: TreemapMetadata;
}

export interface SectorInfo {
  sectors: string[];
  stock_counts: { [sector: string]: number };
  industry_counts: { [sector: string]: number };
}

export const getMarketTreemap = async (
  timeframe: string = '1D',
  level: string = 'sector',
  sector?: string
): Promise<TreemapData> => {
  const params = new URLSearchParams({ timeframe, level });
  if (sector) params.append('sector', sector);

  const response = await api.get(`/api/treemap/market?${params}&_t=${Date.now()}`);
  return response.data;
};

export const getSectorTreemap = async (
  sector: string,
  timeframe: string = '1D'
): Promise<TreemapData> => {
  const response = await api.get(`/api/treemap/sector/${encodeURIComponent(sector)}?timeframe=${timeframe}&_t=${Date.now()}`);
  return response.data;
};

export const getAvailableSectors = async (): Promise<SectorInfo> => {
  const response = await api.get('/api/treemap/sectors');
  return response.data;
};

export const clearTreemapCache = async (): Promise<{ status: string; message: string }> => {
  const response = await api.post('/api/treemap/clear-cache');
  return response.data;
};

export const getIndustryTreemap = async (
  timeframe: string = '1D'
): Promise<TreemapData> => {
  const response = await api.get(`/api/treemap/industries?timeframe=${timeframe}&_t=${Date.now()}`);
  return response.data;
};


// ============================================================================
// FTSE100 TREEMAP API
// ============================================================================

export const getFTSE100MarketTreemap = async (
  timeframe: string = '1D',
  level: string = 'sector',
  sector?: string
): Promise<TreemapData> => {
  const params = new URLSearchParams({ timeframe, level });
  if (sector) params.append('sector', sector);

  const response = await api.get(`/api/treemap/ftse100/market?${params}&_t=${Date.now()}`);
  return response.data;
};

export const getFTSE100SectorTreemap = async (
  sector: string,
  timeframe: string = '1D'
): Promise<TreemapData> => {
  const response = await api.get(`/api/treemap/ftse100/sector/${encodeURIComponent(sector)}?timeframe=${timeframe}&_t=${Date.now()}`);
  return response.data;
};

export const getFTSE100AvailableSectors = async (): Promise<SectorInfo> => {
  const response = await api.get('/api/treemap/ftse100/sectors');
  return response.data;
};

export const clearFTSE100TreemapCache = async (): Promise<{ status: string; message: string }> => {
  const response = await api.post('/api/treemap/ftse100/clear-cache');
  return response.data;
};

export const getFTSE100IndustryTreemap = async (
  timeframe: string = '1D'
): Promise<TreemapData> => {
  const response = await api.get(`/api/treemap/ftse100/industries?timeframe=${timeframe}&_t=${Date.now()}`);
  return response.data;
};


// ============================================================================
// GENERIC MARKET TREEMAP API (supports all markets)
// ============================================================================

export interface MarketConfig {
  code: string;
  country: string;
  name: string;
  currency: string;
}

export interface AvailableMarkets {
  markets: MarketConfig[];
}

export const getAvailableMarkets = async (): Promise<AvailableMarkets> => {
  const response = await api.get('/api/treemap/markets');
  return response.data;
};

export const getGenericMarketTreemap = async (
  market: string,
  timeframe: string = '1D',
  level: string = 'sector',
  sector?: string
): Promise<TreemapData> => {
  const params = new URLSearchParams({ timeframe, level });
  if (sector) params.append('sector', sector);

  const response = await api.get(`/api/treemap/${market}/market?${params}&_t=${Date.now()}`);
  return response.data;
};

export const getGenericSectorTreemap = async (
  market: string,
  sector: string,
  timeframe: string = '1D'
): Promise<TreemapData> => {
  const response = await api.get(`/api/treemap/${market}/sector/${encodeURIComponent(sector)}?timeframe=${timeframe}&_t=${Date.now()}`);
  return response.data;
};

export const getGenericAvailableSectors = async (market: string): Promise<SectorInfo> => {
  const response = await api.get(`/api/treemap/${market}/sectors`);
  return response.data;
};

export const clearGenericTreemapCache = async (market: string): Promise<{ status: string; message: string }> => {
  const response = await api.post(`/api/treemap/${market}/clear-cache`);
  return response.data;
};

export const getGenericIndustryTreemap = async (
  market: string,
  timeframe: string = '1D'
): Promise<TreemapData> => {
  const response = await api.get(`/api/treemap/${market}/industries?timeframe=${timeframe}&_t=${Date.now()}`);
  return response.data;
};


// ============================================================================
// FINVIZ SCREENER TYPES AND API
// ============================================================================

export interface FinvizSnapshot {
  [key: string]: string;
}

export interface FinvizNewsItem {
  title: string;
  link: string;
  date: string;
}

export interface FinvizAnalystTarget {
  date: string;
  category: string;       // Initiated, Upgrade, Downgrade, Reiterated
  analyst: string;        // firm name e.g. "Goldman Sachs"
  rating: string;         // "Buy", "Neutral", "Neutral -> Outperform"
  target_from?: number;   // prior price target
  target_to?: number;     // new price target
}

export interface FinvizInsiderTrade {
  owner: string;
  relationship: string;
  date: string;
  transaction: string;
  cost: number;
  shares: number;
  value: number;
  shares_total: number;
}

export interface StockPulseContext {
  symbol: string;
  name: string;
  sector: string | null;
  country: string | null;
  price: number | null;
  price_lines: string[];
  signals: string[];
  patterns: string[];
  news: string[];
  /** Conflicts between the facts, computed by rules in stock_pulse._tensions.
   *  Rendered verbatim — never paraphrased by the model, which scored 1/5 at
   *  finding these while the rules score 6/6. */
  tensions: string[];
  earnings?: string;
}

export interface StockPulse {
  symbol: string;
  name?: string;
  /** Null when local inference was unavailable — context is still populated. */
  narrative: string | null;
  context: StockPulseContext;
  generated_at?: string;
  elapsed_seconds?: number;
  cached?: boolean;
  error?: string;
}

/** Local-model narrative for a stock. Slow (30-90s uncached) — call with a
 *  generous timeout and show a spinner. `refresh` bypasses the 4h server cache. */
// ── Transcripts (uploaded meeting / call transcripts) ────────────────────────
export interface TranscriptLevel {
  price: number;
  meaning: string;
  /** Set when today's price was available: where it stands against the level. */
  now?: number;
  distance_pct?: number;
  position?: 'above' | 'below' | 'at';
}

export interface TranscriptStockMention {
  company: string;
  ticker: string | null;
  /** How speech recognition rendered the name, when it differs. */
  heard_as?: string | null;
  speaker: string | null;
  stance: string | null;
  view: string | null;
  levels: TranscriptLevel[];
  confidence: 'high' | 'medium' | 'low';
  in_database?: boolean;
  price_now?: number;
  price_date?: string;
}

export interface TranscriptSummary {
  title?: string | null;
  summary: string;
  participants: { name: string; views: string }[];
  key_points: string[];
  stock_mentions: TranscriptStockMention[];
  macro_views: { topic: string; view: string; speaker: string | null }[];
  action_items: { owner: string | null; task: string }[];
  decisions: string[];
  open_questions: string[];
  themes: string[];
}

export interface TranscriptRow {
  id: number;
  title: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  private: boolean;
  meeting_date: string | null;
  word_count: number | null;
  processed_by: string | null;
  error_message: string | null;
  created_at: string | null;
  completed_at: string | null;
  /** From the speaker labels: words spoken and share of the conversation. */
  participants: { name: string; words: number; share: number }[];
  summary?: TranscriptSummary | null;
  raw_text?: string;
}

export const uploadTranscript = async (body: { title?: string; meeting_date?: string; text: string; private: boolean }): Promise<TranscriptRow> =>
  (await api.post('/api/transcripts', body)).data;
/** True if `passcode` opens the transcripts; it is then remembered for this browser session. */
export const unlockTranscripts = async (passcode: string): Promise<boolean> => {
  try {
    await api.post('/api/transcripts/unlock', null, { headers: { 'X-Transcripts-Passcode': passcode } });
    try { sessionStorage.setItem(TRANSCRIPTS_PASSCODE_KEY, passcode); } catch { /* storage blocked */ }
    return true;
  } catch {
    return false;
  }
};
export const listTranscripts = async (): Promise<TranscriptRow[]> => (await api.get('/api/transcripts')).data.transcripts;
export const getTranscript = async (id: number): Promise<TranscriptRow> => (await api.get(`/api/transcripts/${id}`)).data;
export const reprocessTranscript = async (id: number, isPrivate?: boolean): Promise<TranscriptRow> =>
  (await api.post(`/api/transcripts/${id}/reprocess`, isPrivate === undefined ? {} : { private: isPrivate })).data;
export const deleteTranscript = async (id: number): Promise<void> => { await api.delete(`/api/transcripts/${id}`); };
export const downloadTranscriptPdf = async (id: number): Promise<void> => {
  const response = await api.get(`/api/transcripts/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `meeting_${id}.pdf`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ── Ask bar ────────────────────────────────────────────────────────────────
export interface AskHeadline {
  title: string;
  source: string | null;
  url: string | null;
  published_at: string | null;
}

export interface AskScope {
  kind: 'basket' | 'stock' | 'macro';
  label: string;
  symbols: string[];
  theme: string | null;
  topic: string | null;
  horizon: 'today' | 'week' | 'month';
}

export interface AskAnswer {
  matched: boolean;
  question?: string;
  /** Set when the question couldn't be matched to anything in the data. */
  message?: string;
  kind?: 'basket' | 'stock' | 'macro';
  label?: string;
  /** summary = what's happening; implications = what it means; why = causes. */
  mode?: 'summary' | 'implications' | 'why';
  /** Send back as `context` so a follow-up can inherit the subject. */
  scope?: AskScope;
  /** Label of the previous subject when this answer inherited it. */
  followup_of?: string | null;
  /** Rule-based implications (historical tendencies), shown verbatim. */
  implications?: string[];
  /** Holdings each implication bears on, computed from sectors/industries. */
  exposure?: string[];
  /** Human-readable reasons for the match: what the question was read as. */
  interpreted_as?: string[];
  horizon?: 'today' | 'week' | 'month';
  facts?: string[];
  /** Computed by rules and shown verbatim — never written by the model. */
  conflicts?: string[];
  headlines?: AskHeadline[];
  answer?: string | null;
  error?: string | null;
  generated_at?: string;
  elapsed_seconds?: number;
  cached?: boolean;
}

export const askQuestion = async (question: string, context?: AskScope | null): Promise<AskAnswer> => {
  const response = await api.post('/api/ask', { question, context: context || undefined }, {
    // The server fails soft at 280s; the browser must outlast it.
    timeout: 350000,
  });
  return response.data;
};

export const getStockPulse = async (symbol: string, refresh = false): Promise<StockPulse> => {
  const response = await api.get(`/api/stock/${symbol}/pulse`, {
    params: refresh ? { refresh: true } : undefined,
    // 350s: the server fails soft at 300s, so the browser must outlast it or
    // the user sees a connection error for a request that actually succeeded.
    timeout: 350000,
  });
  return response.data;
};

export const getStockSnapshot = async (symbol: string): Promise<FinvizSnapshot> => {
  const response = await api.get(`/api/stock/${symbol}/snapshot`);
  return response.data;
};

export const getFinvizNews = async (symbol: string): Promise<{ symbol: string; news: FinvizNewsItem[] }> => {
  const response = await api.get(`/api/stock/${symbol}/finviz-news`);
  return response.data;
};

export const getStockFinancials = async (symbol: string): Promise<{ symbol: string; financial_statements: any; trading_symbol: string; financial_symbol: string }> => {
  const response = await api.get(`/api/stock/${symbol}/financials`);
  return response.data;
};

// ── Earnings History ──────────────────────────────────────────────────────────
export interface EarningsQuarter {
  report_date: string;
  eps_estimate: number | null;
  eps_actual: number | null;
  eps_surprise_pct: number | null;
  beat_eps: boolean | null;
  revenue_actual: number | null;
  post_earnings_move: number | null;
}

export interface EarningsHistoryResponse {
  symbol: string;
  history: EarningsQuarter[];
  beat_rate: { beats: number; total: number; rate_str: string } | null;
  upcoming_estimates: { eps_estimate: number | null; revenue_estimate: number | null };
}

export const getEarningsHistory = async (symbol: string, quarters = 12): Promise<EarningsHistoryResponse> => {
  const response = await api.get(`/api/stock/${symbol}/earnings-history`, { params: { quarters } });
  return response.data;
};

// ── Earnings Estimates ────────────────────────────────────────────────────────
export interface EarningsEstimates {
  symbol: string;
  earnings_date: string | null;
  eps_estimate: number | null;
  eps_high: number | null;
  eps_low: number | null;
  revenue_estimate: number | null;
  revenue_high: number | null;
  revenue_low: number | null;
  last_quarter_surprise_pct: number | null;
  last_quarter_eps_estimate: number | null;
  last_quarter_eps_actual: number | null;
  num_analysts: number | null;
}

export const getEarningsEstimates = async (symbol: string): Promise<EarningsEstimates> => {
  const response = await api.get(`/api/stock/${symbol}/earnings-estimates`);
  return response.data;
};

// ── Dividends ─────────────────────────────────────────────────────────────────
export interface DividendEntry { date: string; amount: number; }

export interface DividendResponse {
  symbol: string;
  dividend_yield: number | null;
  dividend_rate: number | null;
  payout_ratio: number | null;
  ex_dividend_date: string | null;
  five_year_growth_rate: number | null;
  history: DividendEntry[];
  currency: string;
}

export const getStockDividends = async (symbol: string): Promise<DividendResponse> => {
  const response = await api.get(`/api/stock/${symbol}/dividends`);
  return response.data;
};

export interface PortfolioDividendItem {
  symbol: string;
  company_name: string;
  dividend_yield: number | null;
  dividend_rate: number | null;
  payout_ratio: number | null;
  ex_dividend_date: string | null;
  currency: string;
}

export const getPortfolioDividends = async (symbols: string[]): Promise<PortfolioDividendItem[]> => {
  const response = await api.get('/api/portfolio/dividends', { params: { symbols: symbols.join(',') } });
  return response.data;
};

export const getAnalystTargets = async (symbol: string): Promise<{ symbol: string; targets: FinvizAnalystTarget[] }> => {
  const response = await api.get(`/api/stock/${symbol}/analyst-targets`);
  return response.data;
};

export const getInsiderTrades = async (symbol: string): Promise<{ symbol: string; trades: FinvizInsiderTrade[] }> => {
  const response = await api.get(`/api/stock/${symbol}/insider-trades`);
  return response.data;
};


// ============================================================================
// MACRO OVERVIEW TYPES AND API
// ============================================================================

export interface CountryPerformance {
  country: string;
  country_name: string;
  market: string;
  market_name: string;
  flag: string;
  change: number | null;
  market_cap: number | null;
  stock_count: number;
}

export interface SectorComparison {
  sector: string;
  data: CountryPerformance[];
}

export interface MacroSectorComparisonResponse {
  sectors: SectorComparison[];
  metadata: {
    timeframe: string;
    generated_at: string;
    markets_included: string[];
    total_sectors: number;
  };
}

export interface CountrySummary {
  country: string;
  country_name: string;
  market: string;
  market_name: string;
  flag: string;
  overall_change: number;
  total_market_cap: number;
  sector_count: number;
}

export interface MacroCountrySummaryResponse {
  countries: CountrySummary[];
  metadata: {
    timeframe: string;
    generated_at: string;
  };
}

export interface MacroAvailableSectorsResponse {
  sectors: string[];
  sector_coverage: { [sector: string]: string[] };
}

export const getMacroSectorComparison = async (
  timeframe: string = '1M',
  sectors?: string[]
): Promise<MacroSectorComparisonResponse> => {
  const params = new URLSearchParams({ timeframe });
  if (sectors && sectors.length > 0) {
    params.append('sectors', sectors.join(','));
  }
  const response = await api.get(`/api/macro/sector-comparison?${params}&_t=${Date.now()}`);
  return response.data;
};

export const getMacroCountrySummary = async (
  timeframe: string = '1M'
): Promise<MacroCountrySummaryResponse> => {
  const response = await api.get(`/api/macro/country-summary?timeframe=${timeframe}&_t=${Date.now()}`);
  return response.data;
};

export const getMacroAvailableSectors = async (): Promise<MacroAvailableSectorsResponse> => {
  const response = await api.get('/api/macro/available-sectors');
  return response.data;
};

// Industry comparison types
export interface IndustryComparison {
  industry: string;
  sector: string;
  data: CountryPerformance[];
}

export interface MacroIndustryComparisonResponse {
  industries: IndustryComparison[];
  metadata: {
    timeframe: string;
    generated_at: string;
    markets_included: string[];
    total_industries: number;
    search: string | null;
  };
}

export interface MacroAvailableIndustriesResponse {
  industries: string[];
  industry_sectors: { [industry: string]: string };
  industry_coverage: { [industry: string]: string[] };
}

export const getMacroIndustryComparison = async (
  timeframe: string = '1M',
  industries?: string[],
  search?: string
): Promise<MacroIndustryComparisonResponse> => {
  const params = new URLSearchParams({ timeframe });
  if (industries && industries.length > 0) {
    params.append('industries', industries.join(','));
  }
  if (search) {
    params.append('search', search);
  }
  const response = await api.get(`/api/macro/industry-comparison?${params}&_t=${Date.now()}`);
  return response.data;
};

export const getMacroAvailableIndustries = async (): Promise<MacroAvailableIndustriesResponse> => {
  const response = await api.get('/api/macro/available-industries');
  return response.data;
};


// ============================================================================
// CORRELATION STUDIES TYPES AND API
// ============================================================================

export interface CorrelationComparison {
  symbol1: string;
  symbol2: string;
  historical: number;
  current: number;
  change: number;
  abs_change: number;
}

export interface CorrelationMatrixResponse {
  symbols: string[];
  symbol_names: { [symbol: string]: string };
  matrix: number[][];
  current_matrix: number[][] | null;
  comparison: CorrelationComparison[] | null;
  data_coverage: { [symbol: string]: number };
  missing_symbols: string[];
  timeframe: string;
  days_used: number;
  as_of_date: string | null;
  metadata: {
    generated_at: string;
    data_points: number;
    date_range_start: string | null;
    date_range_end: string | null;
  };
}

export interface IndustryDetail {
  symbol: string;
  name: string;
  weight: number;
}

export interface CrossIndustryResponse extends CorrelationMatrixResponse {
  industry_details: { [industry: string]: IndustryDetail[] };
}

export interface CorrelationHistoryPoint {
  date: string;
  correlation: number;
}

export interface CorrelationHistoryResponse {
  symbol1: string;
  symbol2: string;
  symbol1_name: string;
  symbol2_name: string;
  lookback_period: string;
  history: CorrelationHistoryPoint[];
  statistics: {
    current: number | null;
    average: number;
    min: number;
    max: number;
    std: number;
  } | null;
  error?: string;
}

export interface CorrelationSectorSymbol {
  symbol: string;
  name: string;
  country: string;
  market_cap: number;
}

export interface CorrelationSearchResult {
  symbol: string;
  name: string;
  sector: string | null;
  country: string | null;
}

export const getCorrelationMatrix = async (
  symbols: string[],
  timeframe: string = '1M',
  asOfDate?: string // YYYY-MM-DD format
): Promise<CorrelationMatrixResponse> => {
  const payload: { symbols: string[]; timeframe: string; as_of_date?: string } = {
    symbols,
    timeframe
  };
  if (asOfDate) {
    payload.as_of_date = asOfDate;
  }
  const response = await api.post('/api/correlation/matrix', payload);
  return response.data;
};

export const getCorrelationSectors = async (): Promise<{ sectors: string[] }> => {
  const response = await api.get('/api/correlation/sectors');
  return response.data;
};

export const getCrossIndustryCorrelation = async (
  industries: string[],
  timeframe: string = '1M',
  countries?: string[],
  stocksPerIndustry: number = 10,
  asOfDate?: string
): Promise<CrossIndustryResponse> => {
  const payload: {
    industries: string[];
    timeframe: string;
    countries?: string[];
    stocks_per_industry?: number;
    as_of_date?: string;
  } = { industries, timeframe };
  if (countries && countries.length > 0) {
    payload.countries = countries;
  }
  if (stocksPerIndustry !== 10) {
    payload.stocks_per_industry = stocksPerIndustry;
  }
  if (asOfDate) {
    payload.as_of_date = asOfDate;
  }
  const response = await api.post('/api/correlation/cross-industry', payload);
  return response.data;
};

export const getCorrelationSectorSymbols = async (
  sector: string,
  countries?: string[],
  limit: number = 20
): Promise<{ sector: string; symbols: CorrelationSectorSymbol[] }> => {
  const params = new URLSearchParams();
  if (countries && countries.length > 0) {
    params.append('countries', countries.join(','));
  }
  params.append('limit', limit.toString());

  const response = await api.get(`/api/correlation/sector/${encodeURIComponent(sector)}/symbols?${params}`);
  return response.data;
};

export const searchCorrelationSymbols = async (
  query: string,
  limit: number = 10
): Promise<{ results: CorrelationSearchResult[] }> => {
  const response = await api.get(`/api/correlation/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  return response.data;
};

export const getCorrelationIndustries = async (
  sector?: string,
  countries?: string[]
): Promise<{ industries: string[] }> => {
  const params = new URLSearchParams();
  if (sector) {
    params.append('sector', sector);
  }
  if (countries && countries.length > 0) {
    params.append('countries', countries.join(','));
  }
  const response = await api.get(`/api/correlation/industries?${params}`);
  return response.data;
};

export const getCorrelationIndustrySymbols = async (
  industry: string,
  countries?: string[],
  limit: number = 20
): Promise<{ industry: string; symbols: CorrelationSectorSymbol[] }> => {
  const params = new URLSearchParams();
  if (countries && countries.length > 0) {
    params.append('countries', countries.join(','));
  }
  params.append('limit', limit.toString());

  const response = await api.get(`/api/correlation/industry/${encodeURIComponent(industry)}/symbols?${params}`);
  return response.data;
};

export interface CorrelationIndex {
  code: string;
  name: string;
  country: string;
  constituent_count: number | null;
}

export const getCorrelationIndices = async (): Promise<{ indices: CorrelationIndex[] }> => {
  const response = await api.get('/api/correlation/indices');
  return response.data;
};

export const getCorrelationIndexSymbols = async (
  indexCode: string,
  limit: number = 50
): Promise<{ index_code: string; symbols: CorrelationSectorSymbol[] }> => {
  const response = await api.get(`/api/correlation/index/${encodeURIComponent(indexCode)}/symbols?limit=${limit}`);
  return response.data;
};

export interface BenchmarkSymbol {
  symbol: string;
  name: string;
  category: string;
}

export const getCorrelationBenchmarks = async (): Promise<{ benchmarks: BenchmarkSymbol[] }> => {
  const response = await api.get('/api/correlation/benchmarks');
  return response.data;
};

// ============================================================================
// INDEX MEMBERSHIP API
// ============================================================================

export interface MarketIndexInfo {
  code: string;
  name: string;
  country: string;
  description: string | null;
  constituent_count: number | null;
  last_updated: string | null;
}

export interface IndexConstituent {
  symbol: string;
  company_name: string;
  sector: string | null;
  industry: string | null;
  market_cap: number | null;
}

export const getAvailableIndices = async (): Promise<{ indices: MarketIndexInfo[] }> => {
  const response = await api.get('/api/indices');
  return response.data;
};

export const getIndexConstituents = async (
  indexCode: string,
  limit: number = 50,
  offset: number = 0
): Promise<{ index_code: string; constituents: IndexConstituent[]; total: number }> => {
  const response = await api.get(`/api/indices/${indexCode}/constituents?limit=${limit}&offset=${offset}`);
  return response.data;
};

export const getStockIndices = async (symbol: string): Promise<{ symbol: string; indices: MarketIndexInfo[] }> => {
  const response = await api.get(`/api/stock/${symbol}/indices`);
  return response.data;
};


export const getCorrelationHistory = async (
  symbol1: string,
  symbol2: string,
  lookbackPeriod: string = '1M',
  historyDays: number = 180,
  stepDays: number = 5
): Promise<CorrelationHistoryResponse> => {
  const params = new URLSearchParams({
    symbol1,
    symbol2,
    lookback_period: lookbackPeriod,
    history_days: historyDays.toString(),
    step_days: stepDays.toString()
  });
  const response = await api.get(`/api/correlation/history?${params}`);
  return response.data;
};

// ── Intra-portfolio correlation (model portfolio vs model portfolio) ───────

export interface PortfolioCorrelationMatrixResponse {
  portfolio_ids: number[];
  portfolio_names: { [id: string]: string };
  matrix: number[][];
  missing_portfolios: number[];
  timeframe: string;
  days_used: number;
  avg_pairwise_correlation: number | null;
  diversification_score: number | null;
  metadata: { generated_at: string };
}

export interface PortfolioCorrelationHistoryResponse {
  portfolio_id_1: number;
  portfolio_id_2: number;
  portfolio_name_1: string;
  portfolio_name_2: string;
  lookback_period: string;
  history: CorrelationHistoryPoint[];
  statistics: { current: number; average: number; min: number; max: number; std: number } | null;
  error?: string;
}

export const getPortfolioCorrelationMatrix = async (
  portfolioIds?: number[],
  timeframe: string = '1Y'
): Promise<PortfolioCorrelationMatrixResponse> => {
  const params = new URLSearchParams({ timeframe });
  if (portfolioIds && portfolioIds.length > 0) {
    params.set('portfolio_ids', portfolioIds.join(','));
  }
  const response = await api.get(`/api/correlation/portfolios/matrix?${params}`);
  return response.data;
};

export const getPortfolioCorrelationHistory = async (
  portfolioId1: number,
  portfolioId2: number,
  lookbackPeriod: string = '1M',
  historyDays: number = 180,
  stepDays: number = 5
): Promise<PortfolioCorrelationHistoryResponse> => {
  const params = new URLSearchParams({
    portfolio_id_1: portfolioId1.toString(),
    portfolio_id_2: portfolioId2.toString(),
    lookback_period: lookbackPeriod,
    history_days: historyDays.toString(),
    step_days: stepDays.toString(),
  });
  const response = await api.get(`/api/correlation/portfolios/history?${params}`);
  return response.data;
};

// ── Relative Strength (custom pair analysis) ────────────────────────────────

export interface RsLevel {
  price: number;
  touches: number;
  last_touch: string;
  kind: 'support' | 'resistance';
  score: number;
}

export interface RsLinePoint {
  date: string;
  price: number;
}

export interface RsTrendline {
  kind: 'support' | 'resistance';
  touches: number;
  last_touch: string;
  points: RsLinePoint[];
}

export interface RsChannel {
  direction: 'up' | 'down' | 'flat';
  touches: number;
  upper: RsLinePoint[];
  lower: RsLinePoint[];
}

export interface RsChartResponse {
  symbol_a: string;
  symbol_b: string;
  symbol_a_name?: string;
  symbol_b_name?: string;
  series: { date: string; value: number }[];
  current_value?: number;
  winning_symbol?: string | null;
  pct_gap?: number;
  annotations: { levels: RsLevel[]; trendlines: RsTrendline[]; channels: RsChannel[] };
  days_used?: number;
  error?: string;
}

export interface RsSavedPair {
  id: number;
  symbol_a: string;
  symbol_b: string;
  label: string | null;
  created_at: string | null;
}

export const getRsChart = async (
  symbolA: string,
  symbolB: string,
  timeframe: string = '1Y'
): Promise<RsChartResponse> => {
  const params = new URLSearchParams({ symbol_a: symbolA, symbol_b: symbolB, timeframe });
  const response = await api.get(`/api/relative-strength/chart?${params}`);
  return response.data;
};

export const listRsPairs = async (): Promise<{ pairs: RsSavedPair[] }> => {
  const response = await api.get('/api/relative-strength/pairs');
  return response.data;
};

export const saveRsPair = async (
  symbolA: string,
  symbolB: string,
  label?: string
): Promise<RsSavedPair> => {
  const response = await api.post('/api/relative-strength/pairs', {
    symbol_a: symbolA,
    symbol_b: symbolB,
    label,
  });
  return response.data;
};

export const deleteRsPair = async (pairId: number): Promise<void> => {
  await api.delete(`/api/relative-strength/pairs/${pairId}`);
};

export interface ChartingQuote {
  price: number | null;
  change_pct: number | null;
}

export const getChartingQuotes = async (symbols: string[]): Promise<{ quotes: Record<string, ChartingQuote> }> => {
  if (symbols.length === 0) return { quotes: {} };
  const response = await api.get(`/api/charting/quotes?symbols=${encodeURIComponent(symbols.join(','))}`);
  return response.data;
};

// ============================================================================
// PODCAST TRANSCRIPTION & SUMMARY
// ============================================================================

export type PodcastStatus = 'pending' | 'downloading' | 'transcribing' | 'summarizing' | 'completed' | 'failed';

export interface PodcastSummary {
  podcast_title?: string;
  guests?: string[];
  executive_summary: string;
  key_takeaways: string[];  // legacy
  key_points: string[];
  topics_discussed: string[];
  investment_implications: {
    bullish_signals: string[];
    bearish_signals: string[];
    sectors_mentioned: string[];
    tickers_mentioned: string[];
    macro_themes: string[];
    actionable_ideas: string[];
  };
  asset_allocation: {
    equities_pct: number;
    fixed_income_pct: number;
    commodities_pct: number;
    cash_pct: number;
    alternatives_pct: number;
    rationale: string;
  } | null;
  suggested_trades?: {
    buy: Array<{ asset: string; type: string; rationale: string; conviction: 'high' | 'medium' | 'low' }>;
    avoid: Array<{ asset: string; type: string; rationale: string; conviction: 'high' | 'medium' | 'low' }>;
  } | null;
  suggested_tickers: Array<{
    symbol: string;
    name: string;
    sector: string;
    country: string;
    price: number | null;
    rsi: number | null;
    market_cap: number;
    score: number;
    direction: string;
    reasons: string[];
  }>;
  sentiment_assessment: string;
}

export interface PodcastListItem {
  id: number;
  url: string;
  platform: string | null;
  title: string | null;
  duration_seconds: number | null;
  status: PodcastStatus;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  feed_name: string | null;
  sentiment: string | null;
}

export interface PodcastDetail extends PodcastListItem {
  transcript: string | null;
  summary: PodcastSummary | null;
}

export const submitPodcastTranscription = async (url: string): Promise<{ id: number; status: string; message: string }> => {
  const response = await api.post('/api/podcasts/transcribe', { url });
  return response.data;
};

export const listPodcasts = async (): Promise<PodcastListItem[]> => {
  const response = await api.get('/api/podcasts');
  return response.data;
};

export const getPodcastDetail = async (id: number): Promise<PodcastDetail> => {
  const response = await api.get(`/api/podcasts/${id}`);
  return response.data;
};

export const retryPodcast = async (id: number): Promise<{ id: number; status: string; message: string }> => {
  const response = await api.post(`/api/podcasts/${id}/retry`);
  return response.data;
};

export const deletePodcast = async (id: number): Promise<{ status: string; id: number }> => {
  const response = await api.delete(`/api/podcasts/${id}`);
  return response.data;
};

export const downloadPodcastPdf = async (id: number): Promise<void> => {
  const response = await api.get(`/api/podcasts/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `podcast_${id}_report.pdf`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadWeeklyPodcastDigestPdf = async (days: number = 7): Promise<void> => {
  const response = await api.get(`/api/podcasts/weekly-digest-pdf`, { params: { days }, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `weekly_podcast_digest.pdf`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export interface PortfolioSignalEntry {
  signal_type: string;
  category: string;
  direction: 'bullish' | 'bearish';
  signal_date: string;
  price: number | null;
  change_pct: number | null;
  rsi: number | null;
  sma20_dist: number | null;
  sma50_dist: number | null;
  sma200_dist: number | null;
  detail: string;
}

export interface PortfolioSignalSymbol {
  name: string;
  portfolios: { name: string; weight: number }[];
  signals: PortfolioSignalEntry[];
}

export const getPortfolioSignalsReport = async (days: number = 7): Promise<{
  signals_by_symbol: Record<string, PortfolioSignalSymbol>;
  date_from: string;
  date_to: string;
  count: number;
}> => {
  const response = await api.get('/api/signals/portfolio-report', { params: { days } });
  return response.data;
};

export const downloadPortfolioSignalsPdf = async (days: number = 7, view: string = 'stock'): Promise<void> => {
  const response = await api.get('/api/signals/portfolio-report-pdf', { params: { days, view }, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `portfolio_signals.pdf`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getWhisperStatus = async (): Promise<{ running: boolean; model: string | null; busy?: boolean }> => {
  const response = await api.get('/api/whisper/status');
  return response.data;
};

export const stopWhisperServer = async (): Promise<{ status: string }> => {
  const response = await api.post('/api/whisper/stop');
  return response.data;
};


// ── Model Portfolios ────────────────────────────────────

export type PortfolioRegion = 'US' | 'UK' | 'EU' | 'SWISS' | 'SINGAPORE' | 'KOREA' | 'GLOBAL' | 'THEMATIC';

export interface ModelPortfolioHolding {
  id: number;
  symbol: string;
  name: string;
  target_weight: number;
  role: 'core' | 'satellite';
  added_at: string;
}

export interface ModelPortfolioSummary {
  id: number;
  name: string;
  description: string | null;
  tags: string;
  region: PortfolioRegion | null;
  holdings_count: number;
  total_weight: number;
  created_at: string;
  updated_at: string;
  /** What the model is: theme base, active version, curated theme, regional, conviction, allocation, screen. */
  kind?: ModelPortfolioKind | null;
  /** For an active version: the model it was re-weighted from. */
  parent_id?: number | null;
  theme?: string | null;
  /** For a sub-theme model: the key of its parent theme. */
  theme_parent?: string | null;
  owner?: string | null;
  build_spec?: Record<string, any> | null;
  /** Active section: what it is judged against ("model:<id>" or an ETF symbol). */
  yardstick?: string | null;
}

export type ModelPortfolioKind = 'theme' | 'active' | 'curated_theme' | 'regional' | 'conviction' | 'allocation' | 'screen';

// ── Model league table (backend league_table.py) ──
export type LeaguePeriod = '1W' | '1M' | '3M' | 'YTD' | '1Y';
export interface LeagueRow {
  id: number;
  name: string;
  label: string;
  /** For a sub-theme model: its parent theme's label. */
  parent_label?: string | null;
  kind: ModelPortfolioKind | null;
  theme: string | null;
  theme_parent: string | null;
  region: string | null;
  owner: string | null;
  parent_id: number | null;
  n_holdings: number;
  equal_weight: boolean;
  returns: Record<LeaguePeriod, number | null>;
  returns_week_ago: Record<LeaguePeriod, number | null>;
  vol: number | null;
  sharpe: number | null;
  breadth: number | null;
  coverage: number;
  trend: string | null;
  trend_code: 'up' | 'pullback' | 'bounce' | 'down' | 'cross_up' | 'cross_down' | null;
  mover: { symbol: string | null; r1d: number | null };
}
export interface LeaguePayload { as_of: string | null; risk_free: number; method: string; models: LeagueRow[] }
export interface LeagueHolding {
  symbol: string; name: string; weight: number; history?: number;
  r1d?: number | null; r1m?: number | null; r3m?: number | null; r1y?: number | null;
  above_200?: boolean | null; also_in: string[];
}
export const getModelLeague = async (): Promise<LeaguePayload> =>
  (await api.get('/api/model-portfolios/league', { timeout: 120000 })).data;
export const getLeagueHoldings = async (id: number): Promise<LeagueHolding[]> =>
  (await api.get(`/api/model-portfolios/league/${id}/holdings`)).data.holdings;

// ── Theme rotation map (backend rotation.py) ──
export type RotationQuadrant = 'leading' | 'weakening' | 'lagging' | 'improving';
export interface RotationPoint {
  id: number; label: string; parent_label: string | null; kind: ModelPortfolioKind | null;
  theme: string | null; theme_parent: string | null; benchmark: string;
  x: number; y: number; tail: { date: string; x: number; y: number }[];
  quadrant: RotationQuadrant; prev_quadrant: RotationQuadrant; weeks_in_quadrant: number;
  returns: Record<LeaguePeriod, number | null>;
}
export interface RotationPayload {
  as_of: string | null; benchmark: string; tail_weeks: number; method: string;
  points: RotationPoint[];
  moves: { id: number; label: string; parent_label: string | null; from: RotationQuadrant; to: RotationQuadrant }[];
}
export const getModelRotation = async (benchmark: string, tail: number): Promise<RotationPayload> =>
  (await api.get('/api/model-portfolios/rotation', { params: { benchmark, tail }, timeout: 120000 })).data;

// ── Systematic theme models (backend theme_library.py) ──
export interface ThemeEtf { ticker: string; name: string | null; provider: string | null; holdings: number | null; }
export interface ThemeSummary {
  key: string;
  label: string;
  parent?: string | null;
  etfs: ThemeEtf[];
  model_id: number | null;
  model_name: string | null;
  children?: ThemeSummary[];
}
export const getThemes = async (): Promise<{ snapshot_date: string; themes: ThemeSummary[] }> =>
  (await api.get('/api/themes')).data;
export const buildThemeModel = async (key: string, n?: number, minMcapUsd?: number):
  Promise<{ portfolio_id: number; name: string; holdings: number; skipped: number }> =>
  (await api.post(`/api/themes/${key}/build`, { n, min_mcap_usd: minMcapUsd }, { timeout: 300000 })).data;

export interface ModelPortfolioDetail extends ModelPortfolioSummary {
  holdings: ModelPortfolioHolding[];
  parent_name?: string | null;
  children?: { id: number; name: string; kind: ModelPortfolioKind | null }[];
}

export interface PortfolioScore {
  diversification_score: number;
  avg_pairwise_correlation: number;
  high_correlation_pairs: { symbol1: string; symbol2: string; correlation: number }[];
  concentration_score: number;
  hhi: number;
  max_weight: number;
  num_holdings: number;
  total_weight: number;
  weight_valid: boolean;
}

export interface PerformanceData {
  [symbol: string]: {
    week_1?: number | null;
    month_1?: number | null;
    month_3?: number | null;
    ytd?: number | null;
    year_1?: number | null;
  };
}

export interface HoldingMetric {
  symbol: string;
  weight: number;
  annualized_return: number;
  total_return: number;
  volatility: number;
  sharpe: number;
  return_contribution: number;
  risk_contribution: number;
}

export interface BenchmarkComparison {
  symbol: string;
  name: string;
  total_return: number;
  annualized_return: number;
  volatility: number;
  max_drawdown: number;
  sharpe: number;
  beta: number;
  alpha: number;
  r_squared: number;
  tracking_error: number;
  information_ratio: number | null;
  treynor: number | null;
  correlation: number;
  data_points: number;
}

export interface AvailableBenchmark {
  symbol: string;
  name: string;
  category: string;
}

export interface RiskMetrics {
  data_points: number;
  period_days: number;
  symbols_used: string[];
  total_return: number;
  annualized_return: number;
  cagr: number;
  volatility: number;
  downside_deviation: number;
  max_drawdown: number;
  max_drawdown_duration: number;
  current_drawdown: number;
  var_95: number;
  var_99: number;
  cvar_95: number;
  cvar_99: number;
  sharpe_ratio: number;
  sortino_ratio: number;
  calmar_ratio: number;
  benchmark_comparisons: BenchmarkComparison[];
  skewness: number;
  kurtosis: number;
  positive_days: number;
  negative_days: number;
  win_rate: number;
  avg_win: number;
  avg_loss: number;
  profit_factor: number;
  best_day: number;
  worst_day: number;
  holding_metrics: HoldingMetric[];
  risk_free_rate: number;
  error?: string;
  drawdown_series?: Array<{ date: string; value: number }>;
  returns_distribution?: number[];
  cumulative_returns?: Array<{ date: string; value: number }>;
}

export interface ModelPortfolioAnalysis {
  portfolio: ModelPortfolioDetail;
  correlation: CorrelationMatrixResponse;
  performance: {
    individual: PerformanceData;
    weighted: {
      week_1?: number | null;
      month_1?: number | null;
      month_3?: number | null;
      ytd?: number | null;
      year_1?: number | null;
    };
  };
  score: PortfolioScore;
  risk_metrics: RiskMetrics | null;
}

export const getModelPortfolios = async (): Promise<ModelPortfolioSummary[]> => {
  const response = await api.get('/api/model-portfolios');
  return response.data;
};

export interface PortfolioPerformanceRow {
  id: number;
  name: string;
  holdings_count: number;
  performance?: {
    day_1?: number | null;
    week_1?: number | null;
    month_1?: number | null;
    month_3?: number | null;
    ytd?: number | null;
    year_1?: number | null;
  };
}

export const getPortfoliosPerformance = async (): Promise<PortfolioPerformanceRow[]> => {
  const response = await api.get('/api/model-portfolios/performance');
  return response.data;
};

export const getModelPortfolio = async (id: number): Promise<ModelPortfolioDetail> => {
  const response = await api.get(`/api/model-portfolios/${id}`);
  return response.data;
};

export const createModelPortfolio = async (
  name: string, description?: string, tags?: string, region?: PortfolioRegion | null
): Promise<ModelPortfolioDetail> => {
  const response = await api.post('/api/model-portfolios', { name, description, tags, region });
  return response.data;
};

export const updateModelPortfolio = async (
  id: number, name?: string, description?: string, tags?: string, region?: PortfolioRegion | null
): Promise<ModelPortfolioDetail> => {
  const response = await api.put(`/api/model-portfolios/${id}`, { name, description, tags, region });
  return response.data;
};

export const deleteModelPortfolio = async (id: number): Promise<void> => {
  await api.delete(`/api/model-portfolios/${id}`);
};

export const setModelPortfolioHoldings = async (
  id: number,
  holdings: { symbol: string; target_weight: number; role?: 'core' | 'satellite' }[],
  source: RebalanceSource = 'manual',
  note?: string
): Promise<ModelPortfolioDetail> => {
  const response = await api.put(`/api/model-portfolios/${id}/holdings`, { holdings, source, note });
  return response.data;
};

// Where a set of weights came from (backend REBALANCE_SOURCES).
export type RebalanceSource = 'manual' | 'recommendation' | 'theme_build' | 'frontier' | 'copied';
export const REBALANCE_SOURCE_LABELS: Record<RebalanceSource, string> = {
  manual: 'Manual edit', recommendation: 'Recommendations', theme_build: 'Theme build',
  frontier: 'Efficient Frontier', copied: 'Copied from base',
};

export interface RebalanceEvent {
  id: number;
  effective_at: string;
  source: RebalanceSource;
  note: string | null;
  holdings_count: number;
  holdings: { symbol: string; target_weight: number; role: 'core' | 'satellite' }[];
}

/** Copy a model into a new re-weightable "active" version; the original is untouched. */
export const createActiveVersion = async (id: number): Promise<ModelPortfolioDetail> =>
  (await api.post(`/api/model-portfolios/${id}/active`)).data;

/** Kinds that live in Portfolio › Active, each scored against a yardstick (backend ACTIVE_SECTION_KINDS). */
export const ACTIVE_SECTION_KINDS: ModelPortfolioKind[] = ['active', 'conviction', 'curated_theme', 'regional'];
export const isActiveSection = (kind?: string | null): boolean => !!kind && (ACTIVE_SECTION_KINDS as string[]).includes(kind);

export interface ScoreStats {
  returns?: Record<LeaguePeriod, number | null>; vol?: number | null; sharpe?: number | null;
}
/** What a model is judged against: another model (usually a theme model) or an ETF. */
export interface Yardstick {
  ref: string; kind: 'model' | 'etf'; id: number | null; symbol: string | null;
  /** Short display name: the theme's own name ("Cloud Computing") or the ETF's name, no ticker. */
  label: string;
  /** Full form for tooltips: the model's full name, or "TICKER · ETF name". */
  full?: string;
}
export interface Scorecard {
  id: number; name: string; kind: ModelPortfolioKind | null; owner: string | null; theme: string | null; region: string | null;
  n_holdings: number; top_weight: number | null;
  parent_id: number | null; parent_name: string | null;
  yardstick: Yardstick | null;
  /** Where the real record starts: the last logged weight change, or creation (approximate: before the log). */
  start: { date: string | null; exact: boolean; basis: 'weights_set' | 'created';
    /** The weight change was backdated for testing: the weights used later prices, so it isn't a real record. */
    test?: boolean;
    /** Walk-forward simulation: weights chosen with prices up to the start date only, then tracked. Fair, but not a decision taken at the time. */
    walk_forward?: boolean };
  last_change: { at: string; source: RebalanceSource; note: string | null } | null;
  since_change: { days: number; model_return: number; yardstick_return: number;
    series: { date: string; model: number; yardstick: number }[] } | null;
  backtest: { model: ScoreStats; yardstick: ScoreStats };
  as_of?: string | null;
}
export const getScorecard = async (id: number): Promise<Scorecard> =>
  (await api.get(`/api/model-portfolios/${id}/scorecard`, { timeout: 120000 })).data;
/** ref: "model:<id>", an ETF symbol from the benchmark list, or null to clear. */
export const setYardstick = async (id: number, ref: string | null): Promise<Scorecard> =>
  (await api.put(`/api/model-portfolios/${id}/yardstick`, { yardstick: ref }, { timeout: 120000 })).data;
/** Why a portfolio is ahead of / behind its benchmark, by stock (backend active_explain.attribution). */
export interface AttributionDriver {
  symbol: string; name: string; sector: string; weight: number; benchmark_weight: number;
  return: number; contribution: number; in_portfolio: boolean; in_benchmark: boolean;
}
export interface Attribution {
  id: number; name: string; kind: ModelPortfolioKind | null; benchmark: Yardstick;
  /** "since": the real record; "3m": today's weights over the last 3 months (backtest). */
  window: 'since' | '3m'; start: string | null; end: string; days: number | null;
  record: { test?: boolean; walk_forward?: boolean; exact: boolean; weights_set: string | null };
  portfolio_return: number; benchmark_return: number; difference: number;
  drivers: AttributionDriver[];
  sectors: { sector: string; weight: number; benchmark_weight: number; contribution: number }[];
  buckets: Record<'more_of_winners' | 'more_of_laggards' | 'less_of_winners' | 'less_of_laggards', number>;
  top3_weight: number | null; top3_benchmark_weight: number | null; unpriced: string[];
}
export interface Explanation {
  status: 'idle' | 'running' | 'done' | 'error';
  started?: string; finished?: string; error?: string; cached?: boolean;
  attribution?: Attribution; narrative?: string | null; headlines?: Record<string, string[]>;
  generated_at?: string; elapsed_seconds?: number; model?: string;
}
export const getAttribution = async (id: number): Promise<Attribution> =>
  (await api.get(`/api/model-portfolios/${id}/attribution`, { timeout: 120000 })).data;
/** Start the in-house model's write-up (background job). Returns "done" at once when cached. */
export const startExplain = async (id: number, refresh = false): Promise<Explanation> =>
  (await api.post(`/api/model-portfolios/${id}/explain`, null, { params: { refresh }, timeout: 120000 })).data;
export const getExplainStatus = async (id: number): Promise<Explanation> =>
  (await api.get(`/api/model-portfolios/${id}/explain`)).data;

/** Every Active-section model against its yardstick (the Active Portfolios page). */
export const getActivePortfolios = async (): Promise<{ as_of: string | null; portfolios: Scorecard[] }> =>
  (await api.get('/api/active-portfolios', { timeout: 120000 })).data;

export const getRebalanceHistory = async (id: number): Promise<RebalanceEvent[]> => {
  const response = await api.get(`/api/model-portfolios/${id}/rebalance-history`);
  return response.data;
};

export const detectPortfolioRegion = async (id: number): Promise<PortfolioRegion> => {
  const response = await api.get(`/api/model-portfolios/${id}/detect-region`);
  return response.data.region;
};

export const analyzeModelPortfolio = async (
  id: number, timeframe: string = '1Y', benchmarks?: string[]
): Promise<ModelPortfolioAnalysis> => {
  const body: any = { timeframe };
  if (benchmarks && benchmarks.length > 0) body.benchmarks = benchmarks;
  const response = await api.post(`/api/model-portfolios/${id}/analyze`, body);
  return response.data;
};

export const getAvailableBenchmarks = async (): Promise<AvailableBenchmark[]> => {
  const response = await api.get('/api/benchmarks/available');
  return response.data;
};

export const getPortfolioBenchmarks = async (id: number): Promise<string[]> => {
  const response = await api.get(`/api/model-portfolios/${id}/benchmarks`);
  return response.data;
};

export const setPortfolioBenchmarks = async (id: number, symbols: string[]): Promise<string[]> => {
  const response = await api.put(`/api/model-portfolios/${id}/benchmarks`, { symbols });
  return response.data;
};

export interface WatchlistItem {
  id: number;
  symbol: string;
  name: string;
  sector: string | null;
  industry: string | null;
  market_cap: number | null;
  added_at: string;
}

export const getModelPortfolioWatchlist = async (id: number): Promise<WatchlistItem[]> => {
  const response = await api.get(`/api/model-portfolios/${id}/watchlist`);
  return response.data;
};

export const addToModelPortfolioWatchlist = async (id: number, symbol: string): Promise<{ status: string }> => {
  const response = await api.post(`/api/model-portfolios/${id}/watchlist`, { symbol });
  return response.data;
};

export const removeFromModelPortfolioWatchlist = async (id: number, symbol: string): Promise<void> => {
  await api.delete(`/api/model-portfolios/${id}/watchlist/${symbol}`);
};

export const downloadPortfolioReportPdf = async (
  portfolioId: number,
  timeframe: string = '1Y',
  benchmarks?: string[],
): Promise<void> => {
  const body: Record<string, unknown> = { timeframe };
  if (benchmarks?.length) body.benchmarks = benchmarks;
  const response = await api.post(`/api/model-portfolios/${portfolioId}/report-pdf`, body, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `portfolio_report_${portfolioId}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadComparisonPdf = async (
  portfolioIdA: number,
  portfolioIdB: number,
  timeframe: string = '1Y',
  benchmarks?: string[]
): Promise<void> => {
  const body: any = { portfolio_id_a: portfolioIdA, portfolio_id_b: portfolioIdB, timeframe };
  if (benchmarks && benchmarks.length > 0) body.benchmarks = benchmarks;
  const response = await api.post('/api/model-portfolios/compare-pdf', body, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'portfolio_comparison_report.pdf');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ── Attribution ───────────────────────────────────────────────────────────────

export interface HoldingAttribution {
  symbol: string;
  sector: string;
  weight: number;
  holding_total_return: number;
  holding_ann_return: number;
  benchmark_total_return: number;
  active_return: number;
  active_ann_return: number;
  active_contribution: number;
}

export interface SectorAttribution {
  sector: string;
  total_weight: number;
  active_contribution: number;
  holdings: string[];
  outperformers: number;
  underperformers: number;
}

export interface BrinsonRow {
  sector: string;
  portfolio_weight: number;
  benchmark_weight: number;
  portfolio_sector_return: number;
  benchmark_sector_return: number;
  allocation_effect: number;
  selection_effect: number;
  interaction_effect: number;
  total_active: number;
}

export interface BrinsonTotals {
  allocation_effect: number;
  selection_effect: number;
  interaction_effect: number;
  total_active: number;
}

export interface AttributionResult {
  portfolio_id: number;
  benchmark: string;
  benchmark_name: string;
  timeframe: string;
  period_days: number;
  data_points: number;
  portfolio_total_return: number;
  portfolio_ann_return: number;
  benchmark_total_return: number;
  benchmark_ann_return: number;
  active_return: number;
  n_holdings: number;
  n_outperformers: number;
  n_underperformers: number;
  holdings: HoldingAttribution[];
  sectors: SectorAttribution[];
  brinson?: BrinsonRow[];
  brinson_totals?: BrinsonTotals;
}

export const getPortfolioAttribution = async (
  portfolioId: number,
  benchmark: string,
  timeframe: string = '1Y',
): Promise<AttributionResult> => {
  const response = await api.get(`/api/model-portfolios/${portfolioId}/attribution`, {
    params: { benchmark, timeframe },
  });
  return response.data;
};

export interface UniverseNewsArticle {
  id: number;
  url: string | null;
  title: string;
  summary: string | null;
  source: string;
  source_url: string | null;
  symbol: string | null;
  related_tickers: string[];
  category: string | null;
  sentiment: string | null;
  sentiment_score: number | null;
  quality_score: number | null;
  is_breaking: boolean;
  published_at: string | null;
  is_read: boolean;
}

export interface UniverseNewsResponse {
  articles: UniverseNewsArticle[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  available_sectors: string[];
  available_countries: string[];
  portfolios: { id: number; name: string }[];
}

export const getUniverseNews = async (params: {
  portfolio_id?: number | null;
  sector?: string;
  country?: string;
  content_type?: string;
  search?: string;
  breaking_only?: boolean;
  sort?: 'recent' | 'quality';
  page?: number;
  page_size?: number;
}): Promise<UniverseNewsResponse> => {
  const response = await api.get('/api/model-portfolios/universe/news', { params });
  return response.data;
};

// ============================================================================
// MACRO TIME SERIES (FRED / OECD / DBnomics / CFTC / CBOE / AAII)
// ============================================================================

/** Direction that counts as the benign reading — drives arrow colouring. */
export type MacroGoodWhen = 'up' | 'down' | null;

export interface MacroChange {
  abs: number | null;
  /** Suppressed (null) where a percentage would be nonsense — a % of a %, or
   *  positioning that crosses zero. Fall back to `abs` when this is null. */
  pct: number | null;
}

export interface MacroLatest {
  date: string;
  value: number;
  changes: Record<'1d' | '1w' | '1m' | '3m' | '1y' | 'ytd', MacroChange>;
}

export interface MacroSeriesMeta {
  id: string;
  name: string;
  unit: string;          // % | idx | ratio | k | m | count | pts | contracts
  group: string;
  group_label: string;
  source: string;
  transform: string;
  good_when: MacroGoodWhen;
  note: string;
}

export interface MacroOverviewSeries extends MacroSeriesMeta {
  latest: MacroLatest | null;
  /** 48 evenly-sampled values over the last year. Shape only — no axis. */
  sparkline: number[];
  /** Median gap between recent observations, measured from the data. Lets the
   *  UI judge staleness against the series' own rhythm rather than a flat cutoff. */
  cadence_days: number | null;
}

export interface MacroOverviewResponse {
  groups: { id: string; label: string }[];
  series: MacroOverviewSeries[];
}

export interface MacroRecessionBand {
  start: string;
  end: string;
}

export interface MacroSeriesResponse extends MacroSeriesMeta {
  points: { d: string; v: number }[];
  latest: MacroLatest | null;
  recession_bands?: MacroRecessionBand[];
  empty?: boolean;
}

export interface MacroStatusRow {
  id: string;
  name: string;
  source: string;
  healthy: boolean;
  last_success: string | null;
  last_error: string | null;
  last_error_at: string | null;
  point_count: number;
  latest_obs_date: string | null;
}

/** Latest value + changes + sparkline for many series in one call. */
export const getMacroOverview = async (
  opts: { group?: string; ids?: string[] } = {}
): Promise<MacroOverviewResponse> => {
  const params = new URLSearchParams();
  if (opts.group) params.append('group', opts.group);
  if (opts.ids?.length) params.append('ids', opts.ids.join(','));
  const response = await api.get(`/api/macro/overview?${params}&_t=${Date.now()}`);
  return response.data;
};

/** One series, transformed and windowed, with NBER recession bands. */
export const getMacroSeries = async (
  seriesId: string,
  years: number = 10
): Promise<MacroSeriesResponse> => {
  const response = await api.get(`/api/macro/series/${seriesId}?years=${years}`);
  return response.data;
};

export const getMacroStatus = async (): Promise<{
  series: MacroStatusRow[];
  healthy: number;
  total: number;
}> => {
  const response = await api.get(`/api/macro/status?_t=${Date.now()}`);
  return response.data;
};

export interface MoverScopeCountry {
  code: string;
  name: string;
  region: string;
  /** Constituents actually rankable in this country, after the movers filters. */
  count: number;
}

export interface MoverScope {
  value: string;
  label: string;
  countries: MoverScopeCountry[];
}

export default api;
